// Касса Dastarhan 2.0. Машина состояний: активация → PIN → смена → заказ ⇄ оплата.
// Каждое действие с деньгами сначала ложится в офлайн-журнал, потом уходит
// на сервер; идентификаторы — ULID, повторная отправка безопасна.
import { StrictMode, useCallback, useEffect, useRef, useState } from 'react';
import { ServerSetup } from './screens/ServerSetup';
import { registerServiceWorker } from './offline/register-sw';
import { tt } from './i18n';
import { installCrashReporter } from './crash-report';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { applyPromos, formatMoney, moneyInWords, orderTotals, policyOf, receiptLines, reduceOrder, type OrderMode, type OrderState, ulid, wrap, serviceApplies, resolveRules, canTouchLine, hasFired, freshCount, stationOf } from '@dastarhan/core';
import { actLines, xReportLines, zReportLines } from '@dastarhan/core';
import { CatalogResponse } from '@dastarhan/contracts';
import type { GuestLookupResponse, PermissionMap, SyncEvent } from '@dastarhan/contracts';
import { UpdateBanner } from './UpdateBanner';
import './pos.css';
import { api, ApiError, BASE } from './api';
import { enqueue, flush, flushEx, pendingCount } from './offline/queue';
import { pinPrint, readPass, savePass } from './offline/session';
import { Activate } from './screens/Activate';
import { Pin, type ClockOutResult } from './screens/Pin';
import { ShiftGate } from './screens/ShiftGate';
import { OrderScreen } from './screens/Order';
import { ShiftSheet } from './screens/ShiftSheet';
import { ShiftClose } from './screens/ShiftClose';
import { ChecksScreen } from './screens/Checks';
import { HallMap } from './screens/HallMap';
import { BillDialog } from './screens/BillDialog';
import { ReassignDialog } from './screens/ReassignDialog';
import { TextPrompt } from './screens/Prompts';
import { VoidFlow } from './void/VoidFlow';
import { CardDialog } from './screens/CardDialog';
import { OpenTablesScreen } from './screens/OpenTables';
import { PaidScreen, type PaidInfo } from './screens/PaidScreen';
import { TransferDialog } from './screens/TransferDialog';
import { TableDialog } from './screens/TableDialog';
import { DiscountDialog } from './screens/DiscountDialog';
import { StopDialog } from './screens/StopDialog';
import { PrinterDialog } from './screens/PrinterDialog';
import { GuestDialog } from './screens/GuestDialog';
import { SplitDialog } from './screens/SplitDialog';
import { WeightDialog } from './screens/WeightDialog';
import { ElevateDialog } from './screens/ElevateDialog';
import { PayScreen, type PaymentDraft } from './screens/Pay';
import { agentWasSilent, isDesktopApp, isVirtualPrinter, paperWidth, printReceipt, printRunner, printSmart } from './print';

const LS = {
  deviceKey: 'dastarhan.deviceKey',
  place: 'dastarhan.place',
  terminalId: 'dastarhan.terminalId',
  location: 'dastarhan.location',
  waiter: 'dastarhan.waiter',
  token: 'dastarhan.token',
  user: 'dastarhan.user',
  perms: 'dastarhan.perms',
  discountLimit: 'dastarhan.discountLimit',
  referral: 'dastarhan.referral',
  menuUrl: 'dastarhan.menuUrl',
  shiftId: 'dastarhan.shiftId',
};

const qc = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

const seqKey = (shiftId: string) => `dastarhan.seq.${shiftId}`;

/** Номер чека — порядковый в смене. Резервируется при открытии заказа,
 *  фиксируется оплатой; брошенный без оплаты заказ номер не съедает. */
function peekNumber(shiftId: string): number {
  return Number(localStorage.getItem(seqKey(shiftId)) ?? '0') + 1;
}
function commitNumber(shiftId: string, n: number): void {
  // Монотонность: отложенные заказы оплачиваются не по порядку,
  // и откат счётчика назад означал бы дубль номера чека
  const cur = Number(localStorage.getItem(seqKey(shiftId)) ?? '0');
  localStorage.setItem(seqKey(shiftId), String(Math.max(cur, n)));
}

/** Отложенный заказ стола: касса ведёт несколько столов сразу,
 *  перезагрузка страницы ничего не теряет (localStorage). */
interface ParkedOrder {
  table: { id: string; name: string };
  order: OrderState;
  discount: number;
  guest: GuestLookupResponse | null;
  /**
   * Когда напечатали счёт. Стол вернётся к официанту в том же
   * состоянии: с красной меткой и кнопкой возврата, а не «как новый»
   */
  billedAt?: number | null;
}
const parkedKey = (shiftId: string) => `dastarhan.parked.${shiftId}`;
/*
 * Черновик: то, что официант набирает ПРЯМО СЕЙЧАС.
 *
 * Отложенные столы давно переживают перезапуск, а набираемый чек жил
 * только в памяти: планшет уснул, касса вылетела, свет моргнул — и
 * набор пропал вместе с интернетом. Держим его рядом с отложенными,
 * тем же ключом смены: пережить обязано всё, что уже введено руками.
 */
const draftKey = (shiftId: string) => `dastarhan.draft.${shiftId}`;
type Draft = {
  order: OrderState;
  table: { id: string; name: string } | null;
  discount: number;
  guest: GuestLookupResponse | null;
  billedAt: number | null;
};
function loadDraft(shiftId: string): Draft | null {
  try {
    const raw = localStorage.getItem(draftKey(shiftId));
    if (!raw) return null;
    const d = JSON.parse(raw) as Draft;
    // Пустой черновик восстанавливать незачем — начинаем с чистого листа
    return d.order?.items?.some((i) => !i.removed) ? d : null;
  } catch { return null; }
}
function loadParked(shiftId: string): ParkedOrder[] {
  try {
    const raw = localStorage.getItem(parkedKey(shiftId));
    return raw ? (JSON.parse(raw) as ParkedOrder[]) : [];
  } catch { return []; }
}

/*
 * «На кухне» переживает выход официанта и даже старый сервер.
 *
 * Болезнь: официант заказал, вышел, вернулся — и кнопка «Заказать»
 * снова горела, а повтор слал те же блюда на кухню второй раз.
 * Сервер терял построчный флаг при зеркалировании стола.
 * Теперь флаг едет в контракте, а на самом планшете живёт памятка
 * по заказу — страховка, пока сервер не обновлён. Старые записи
 * вычищаются сами: чек живёт вечер, а не неделю.
 */
const FIRED_LS = 'dastarhan.firedByOrder';
type FiredMap = Record<string, { at: number; ids: string[] }>;
function readFiredMap(): FiredMap {
  try { return JSON.parse(localStorage.getItem(FIRED_LS) ?? '{}') as FiredMap; } catch { return {}; }
}
function rememberFired(orderId: string, ids: string[]): void {
  const m = readFiredMap();
  const cur = new Set(m[orderId]?.ids ?? []);
  for (const id of ids) cur.add(id);
  m[orderId] = { at: Date.now(), ids: [...cur] };
  for (const [k, v] of Object.entries(m)) {
    if (Date.now() - v.at > 2 * 86400_000) delete m[k];
  }
  try { localStorage.setItem(FIRED_LS, JSON.stringify(m)); } catch { /* квота — не роняем кассу */ }
}
function firedIdsOf(orderId: string): Set<string> {
  return new Set(readFiredMap()[orderId]?.ids ?? []);
}

/** Зеркалирование стола на сервер — чтобы другие кассы видели.
 *  Локальная копия главнее; сетевые ошибки глушим: офлайн не мешает. */
function mirrorPark(token: string, entry: ParkedOrder): void {
  const live = entry.order.items.filter((i) => !i.removed);
  void api.openOrderPut(token, {
    orderId: entry.order.orderId,
    tableId: entry.table.id,
    tableName: entry.table.name,
    number: entry.order.number,
    openedAt: entry.order.openedAt,
    items: live.map((i) => ({
      itemId: i.itemId, productId: i.productId, name: i.name,
      qty: i.qty, unitPrice: i.unitPrice, modifiers: i.modifiers,
      ...(i.fired ? { fired: true } : {}),
    })),
    discount: entry.discount,
    ...(entry.guest ? { guestPhone: entry.guest.phone } : {}),
  }).catch(() => undefined);
}
function mirrorUnpark(token: string, orderId: string): void {
  void api.openOrderTake(token, orderId).catch(() => undefined);
}

function newOrder(shiftId: string): OrderState {
  return reduceOrder(null, {
    type: 'opened',
    orderId: ulid(),
    number: peekNumber(shiftId),
    mode: 'DINE_IN',
    at: new Date().toISOString(),
  });
}

function Pos({ token, user, perms, onLogout }: {
  token: string;
  user: { id: string; fullName: string };
  perms: PermissionMap;
  onLogout: () => void;
}) {
  const terminalId = localStorage.getItem(LS.terminalId) ?? '';
  const [shiftId, setShiftId] = useState<string | null>(localStorage.getItem(LS.shiftId));
  // Черновик прошлого запуска — если он был, начинаем с него
  const restored = loadDraft(localStorage.getItem(LS.shiftId) ?? 'draft');
  const [order, setOrder] = useState<OrderState>(
    () => restored?.order ?? newOrder(localStorage.getItem(LS.shiftId) ?? 'draft'));
  const [viewChecks, setViewChecks] = useState(false);
  const [table, setTable] = useState<{ id: string; name: string } | null>(restored?.table ?? null);
  const [pickingTable, setPickingTable] = useState(false);
  const [discount, setDiscount] = useState(restored?.discount ?? 0);
  const [discountFlow, setDiscountFlow] = useState<null | 'elevate' | 'dialog'>(null);
  const [printerOpen, setPrinterOpen] = useState(false);
  const [stopOpen, setStopOpen] = useState(false);
  const [guest, setGuest] = useState<GuestLookupResponse | null>(restored?.guest ?? null);
  /**
   * Пречек одной функцией: кнопка «Счёт» и Ctrl+P зовут её же.
   *
   * `mark` разделяет два случая. Гость попросил счёт посмотреть —
   * печатаем, стол остаётся в работе, он ещё дозакажет. Официант
   * закрывает счёт — печатаем И помечаем стол ждущим денег: дальше
   * его откроет кассир, а официант к нему не вернётся.
   */
  const doPrecheck = (mark = true, print = true) => {
        // Снимок суммы и время — только при закрытии: по ним касса
        // понимает, что состав изменился после счёта. При простой
        // печати менять ещё можно, и предупреждать не о чем
        if (mark) {
          setPrecheckSnap(t.total);
          setPrecheckAt(Date.now());
        }
        // Счёт напечатан — стол ждёт денег, а не заказа. Отмечаем на сервере,
        // чтобы это увидели все: официант со схемы, кассир из списка столов
        if (mark && table && shiftId) {
          void api.openOrderPut(token, {
            orderId: order.orderId, tableId: table.id, tableName: table.name,
            number: order.number, openedAt: order.openedAt,
            items: order.items.filter((i) => !i.removed).map((i) => ({
              itemId: i.itemId, productId: i.productId, name: i.name,
              qty: i.qty, unitPrice: i.unitPrice, modifiers: i.modifiers,
            })),
            discount, precheck: true,
          }).catch(() => undefined);
        }
        if (print) void printSmart(receiptLines({
            lang: (localStorage.getItem('dastarhan.receiptLang') as 'ru' | 'kk') ?? 'ru',
          place: localStorage.getItem(LS.place) ?? 'Dastarhan',
          number: order.number,
          cashier: user.fullName,
          // Официант и время открытия — как на канонических чеках
          ...(order.openedBy?.name ? { waiter: order.openedBy.name } : {}),
          ...(table ? { table: table.name } : {}),
          ...(guest?.name ? { guestName: guest.name } : {}),
          at: new Date(),
          openedAt: new Date(order.openedAt),
          items: order.items.filter((i) => !i.removed),
          ...(allPromos.length > 0 ? { promos: allPromos } : {}),
          ...(discount > 0 ? { discount } : {}),
          ...(t.service > 0 ? { service: t.service, servicePct: t.servicePct } : {}),
          precheck: true,
          total: t.total,
          payments: [],
          feedbackQr: Boolean(localStorage.getItem(LS.menuUrl)),
        }, paperWidth()), localStorage.getItem(LS.menuUrl)
          ? `${localStorage.getItem(LS.menuUrl)}?fb=${order.orderId}`
          : undefined);
    say(agentWasSilent()
      ? 'Счёт отправлен, но агент печати молчал — проверьте ⚙'
      : 'Счёт напечатан — Ctrl+P повторит');
  };
  // слушатель живёт в []-эффекте — свежую функцию несёт ref
  const doPrecheckRef = useRef(doPrecheck);
  doPrecheckRef.current = doPrecheck;
  // Ctrl+Enter: к оплате, если есть за что — свежесть тем же ref-приёмом
  /**
   * Вернуть стол официанту.
   *
   * Кассир открыл чужой стол, увидел ошибку — лишнее блюдо, не тот
   * состав — и отдаёт назад. Официант исправит и закроет счёт заново.
   *
   * Заказ возвращается ЕМУ, а не переезжает в выручку кассира: он его
   * вёл, ему и отвечать. Стол снова горит как занятый.
   */
  const giveBack = async () => {
    if (!table || !order.openedBy) return;
    const live = order.items.filter((i) => !i.removed);
    if (live.length === 0) return;
    try {
      await api.openOrderPut(token, {
        orderId: order.orderId, tableId: table.id, tableName: table.name,
        number: order.number, openedAt: order.openedAt,
        items: live.map((i) => ({
          itemId: i.itemId, productId: i.productId, name: i.name,
          qty: i.qty, unitPrice: i.unitPrice, modifiers: i.modifiers,
        })),
        discount,
        keepOwner: order.openedBy.id,
      });
      setOrder(newOrder(localStorage.getItem(LS.shiftId) ?? 'draft'));
      setTable(null);
      setDiscount(0);
      setGuest(null);
      setCard(null);
      setPaying(false);
      setBillOpen(false);
      say(`Стол ${table.name} возвращён: ${order.openedBy.name}`);
    } catch {
      alert('Не получилось вернуть. Проверьте связь');
    }
  };

  /**
   * Уйти со стола после закрытия счёта.
   *
   * Официант отдал бумагу и пошёл за деньгами — держать его на этом
   * заказе незачем: править состав уже нельзя, а стол ждёт кассира.
   * Возвращаем к списку столов, чтобы он сразу взял следующий.
   */
  const leaveTable = () => {
    const sid = localStorage.getItem(LS.shiftId) ?? 'draft';
    setOrder(newOrder(sid));
    setTable(null);
    setDiscount(0);
    setGuest(null);
    setCard(null);
    if (waiter) setOpenTables(true);
  };

  const openPayRef = useRef<() => void>(() => undefined);

  // Тост: короткая подсказка о том, что действие обратимо.
  // У важных тостов есть кнопка «Вернуть»: планшет без клавиатуры
  // не знает Ctrl+Z, а палец промахивается именно на планшете
  const [toast, setToast] = useState<string | null>(null);
  const [toastUndo, setToastUndo] = useState<(() => void) | null>(null);
  const [keysHelp, setKeysHelp] = useState(false);
  // Две вкладки кассы — два счётчика номеров на одну смену
  const [twinTab, setTwinTab] = useState(false);
  useEffect(() => {
    const id = Math.random().toString(36).slice(2);
    const KEY = 'dastarhan.tab';
    localStorage.setItem(KEY, JSON.stringify({ id, at: Date.now() }));
    const onStorage = (e: StorageEvent) => {
      if (e.key !== KEY || !e.newValue) return;
      try {
        const v = JSON.parse(e.newValue) as { id: string };
        if (v.id !== id) setTwinTab(true);
      } catch { /* чужой формат — молчим */ }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const sayRef = useRef<(m: string, undo?: () => void) => void>(() => undefined);
  const say = (msg: string, undo?: () => void) => {
    setToast(msg);
    setToastUndo(() => (undo ?? null));
    // с кнопкой «Вернуть» тост живёт дольше: его надо успеть нажать
    window.setTimeout(() => setToast((cur) => (cur === msg ? null : cur)), undo ? 5000 : 3000);
  };

  sayRef.current = say;

  // выручка смены нарастающим итогом — переживает перезагрузку в LS
  const sumKey = shiftId ? `dastarhan.shiftSum.${shiftId}` : null;
  const [shiftSum, setShiftSum] = useState<number>(() =>
    sumKey ? Number(localStorage.getItem(sumKey) ?? 0) : 0);
  const bumpShiftSum = (v: number) => {
    if (!sumKey) return;
    setShiftSum((prev) => {
      const next = prev + v;
      localStorage.setItem(sumKey, String(next));
      return next;
    });
  };

  // Ctrl+Z: последние три добора можно снять — палец промахивается
  const undoStack = useRef<OrderState[]>([]);
  const pushUndo = (o: OrderState) => {
    undoStack.current = [...undoStack.current.slice(-2), o];
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setKeysHelp((v) => !v); // вторая ⌘/ закрывает — переключатель, не модалка
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        openPayRef.current();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
        e.preventDefault(); // браузерная печать страницы кассе не нужна
        doPrecheckRef.current();
        return;
      }
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return;
      const prev = undoStack.current.pop();
      if (prev) { e.preventDefault(); setOrder(prev); sayRef.current('Вернули как было'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // сумма на момент печати пречека: разошлась — пречек устарел
  const [precheckSnap, setPrecheckSnap] = useState<number | null>(null);
  // Во сколько печатали счёт: гость держит бумагу с этим временем,
  // и если состав изменился, разговор пойдёт именно об этом
  const [precheckAt, setPrecheckAt] = useState<number | null>(restored?.billedAt ?? null);
  /**
   * Стол взят с сервера, а не набран здесь.
   *
   * Кассир открыл чужой стол для оплаты — вернуть его официанту нужно
   * даже без печати счёта: гость подошёл платить, передумал и решил
   * взять ещё чаю.
   */
  const [takenTable, setTakenTable] = useState(false);
  useEffect(() => { setPrecheckSnap(null); }, [order.orderId]);
  const [showMap, setShowMap] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  // Все открытые столы зала: официант ушёл, а гость хочет счёт
  const [openTables, setOpenTables] = useState(false);
  // Право брать деньги: официант набирает заказ и отправляет на кухню,
  // но закрыть чек может не он. Подтверждение старшего снимает запрет
  // на один чек — дальше право снова спрашивается
  const [payApproved, setPayApproved] = useState(false);
  const [payElevate, setPayElevate] = useState(false);
  // Счёт показывается ДО печати: официант сверяет состав со столом,
  // а не читает его с уже вышедшей ленты
  const [billOpen, setBillOpen] = useState(false);
  // Передача стола сменщику: официант уходит, гости остаются
  const [reassignOpen, setReassignOpen] = useState(false);
  // Отказ от блюда: сначала выбрать какое, потом причину. Двумя шагами,
  // потому что в чеке бывает десять строк и промахнуться легко

  // Снятие позиции с причиной: за каждым снятым блюдом стоят деньги
  // Отказ от блюда: одно окно, позиция выбирается внутри
  // Отказ от блюда: null — закрыт, иначе — открыт с предвыбором
  const [voidFlow, setVoidFlow] = useState<{ preselect: string | null } | null>(null);
  // Заметки кухне через свой диалог: prompt на планшете в режиме киоска
  // часто заблокирован, а где работает — без клавиатуры под палец
  const [noteFor, setNoteFor] = useState<{ itemId: string | null; initial: string } | null>(null);
  const [promoPrompt, setPromoPrompt] = useState(false);
  // Скидка по карте постоянного гостя: живёт рядом с ручной скидкой,
  // но приходит от карты и потому подписана иначе
  const [card, setCard] = useState<{ cardId: string; percent: number; label: string } | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  // Правила выбранного стола: приходят вместе с картой зала
  const [tableRules, setTableRules] = useState<{
    servicePct: number | null; discountPct: number | null;
    hallServicePct: number | null; hallDiscountPct: number | null;
  } | null>(null);



  // Итог после оплаты: сдача и состав остаются на экране,
  // пока кассир отвечает гостю
  const [paid, setPaid] = useState<PaidInfo | null>(null);
  // Строки последнего чека: копия печатает их же, а не пересобранные
  const [lastLines, setLastLines] = useState<string[]>([]);
  const [pickingGuest, setPickingGuest] = useState(false);
  const [pendingDiscount, setPendingDiscount] = useState<number | null>(null);
  const referralCfg = (() => {
    try {
      const raw = localStorage.getItem(LS.referral);
      return raw ? (JSON.parse(raw) as { everyVisits: number; percent: number }) : null;
    } catch { return null; }
  })();
  const discountLimitPct = (() => {
    const raw = localStorage.getItem(LS.discountLimit);
    return raw ? Number(raw) : null;
  })();
  const [parked, setParked] = useState<ParkedOrder[]>(() =>
    shiftId ? loadParked(shiftId) : []);
  useEffect(() => {
    if (shiftId) localStorage.setItem(parkedKey(shiftId), JSON.stringify(parked));
  }, [parked, shiftId]);
  /*
   * Черновик пишется на каждое движение руки: добавили позицию,
   * выбрали стол, дали скидку. Планшет может уснуть в любую секунду —
   * набранное не должно зависеть от того, успел ли официант отложить
   */
  useEffect(() => {
    if (!shiftId) return;
    const live = order.items.filter((i) => !i.removed);
    if (live.length === 0) { localStorage.removeItem(draftKey(shiftId)); return; }
    try {
      localStorage.setItem(draftKey(shiftId), JSON.stringify({
        order, table, discount, guest, billedAt: precheckAt,
      } satisfies Draft));
    } catch { /* хранилище тесно — набор всё равно на экране */ }
  }, [order, table, discount, guest, precheckAt, shiftId]);
  const liveCount = order.items.filter((i) => !i.removed).length;

  const parkCurrent = () => {
    // Запоминаем, был ли напечатан счёт: стол вернётся к официанту
    // в том же состоянии, а не «как новый»
    if (!shiftId || !table || liveCount === 0) return;
    const entry: ParkedOrder = { table, order, discount, guest, billedAt: precheckAt };
    setParked((p) => [...p.filter((x) => x.table.id !== table.id), entry]);
    mirrorPark(token, entry);
    setOrder(newOrder(shiftId));
    setTable(null);
    setDiscount(0);
    setGuest(null);
    setDeposit(null);
    setPromoApplied(null);
  };

  const [splitting, setSplitting] = useState(false);
  const [waiterMode, setWaiter] = useState(() => localStorage.getItem(LS.waiter) === '1');

  /**
   * Вид кассы: официантский или кассовый.
   *
   * Уклад настраивается на устройстве, но ПРАВО решает сильнее.
   * У кого нет права брать деньги — тому и кнопки оплаты видеть незачем,
   * даже если он подошёл к стойке, где стоит кассовый режим. Иначе
   * официант жмёт «Оплатить», получает запрос PIN и не понимает, что
   * сделал не так.
   */
  const waiter = waiterMode || policyOf(perms, 'order.pay') !== 'allowed';
  // В настройках показываем waiterMode — своё значение переключателя.
  // Иначе кассир увидит включённый уклад официанта, которого не ставил
  // Уклад работы кассы. В зале официант ведёт несколько столов и после
  // заказа хочет видеть список; за стойкой кассир бьёт чек за чеком
  // и хочет чистый экран. Настройка живёт на устройстве: у стойки
  // и у официанта разные телефоны и разные привычки
  const [afterOrder, setAfterOrder] = useState<'list' | 'desk'>(
    () => (localStorage.getItem('dastarhan.afterOrder') as 'list' | 'desk') ?? 'desk',
  );
  const [weighing, setWeighing] = useState<{
    p: { id: string; name: string; price: number; unit: 'KG' | 'L' };
    mods: import('@dastarhan/core').ItemModifier[];
  } | null>(null);
  /** Сплит: отмеченное остаётся в текущем чеке, остальное паркуется
   *  на этот же стол новым заказом. Оба номера жжём сразу — уникальность
   *  номера чека дороже красоты нумерации. */
  const splitOrder = (keepIds: string[]) => {
    setSplitting(false);
    if (!shiftId || !table) return;
    const keep = new Set(keepIds);
    const live = order.items.filter((i) => !i.removed);
    const rest = live.filter((i) => !keep.has(i.itemId));
    if (rest.length === 0 || rest.length === live.length) return;
    const restOrder: OrderState = {
      ...order,
      orderId: ulid(),
      number: order.number + 1,
      openedAt: new Date().toISOString(),
      items: rest,
    };
    commitNumber(shiftId, order.number + 1);
    const restEntry: ParkedOrder = { table, order: restOrder, discount: 0, guest: null };
    setParked((p) => [...p.filter((x) => x.table.id !== table.id), restEntry]);
    mirrorPark(token, restEntry);
    setOrder((o) => ({ ...o, items: o.items.filter((i) => i.removed || keep.has(i.itemId)) }));
  };

  const [promoApplied, setPromoApplied] = useState<{ code: string; amount: number } | null>(null);
  const [deposit, setDeposit] = useState<{ reservationId: string; amount: number; guestName: string } | null>(null);

  const pickTable = (
    t: { id: string; name: string } | null,
    resv?: { id: string; deposit: number; guestName: string },
  ) => {
    setPickingTable(false);
    if (!t) { setTable(null); return; }
    const held = parked.find((x) => x.table.id === t.id);
    if (held) {
      /*
       * Раньше здесь стоял запрет: «сначала отложите или оплатите
       * текущий». Официант с подносом в руках упирался в стену и
       * должен был вручную делать то, что касса знает сама.
       *
       * Теперь два честных исхода. Набранное принадлежит столу —
       * откладываем его на свой стол и открываем нужный. Набранное
       * ничьё (стол ещё не выбран) — доносим его гостям этого стола:
       * ровно то, зачем официант и шёл
       */
      if (liveCount > 0 && table && table.id !== t.id) {
        parkCurrent();
      } else if (liveCount > 0 && !table) {
        const merged: OrderState = {
          ...held.order,
          items: [...held.order.items, ...order.items.filter((i) => !i.removed)],
        };
        setParked((ps) => ps.filter((x) => x.table.id !== t.id));
        mirrorUnpark(token, held.order.orderId);
        setOrder(merged);
        setTable(held.table);
        setDiscount(held.discount);
        setGuest(held.guest);
        setPrecheckAt(held.billedAt ?? null);
        setToast(`Добавлено к заказу стола ${t.name}`);
        return;
      }
      // Возобновляем отложенный заказ стола
      setParked((p) => p.filter((x) => x.table.id !== t.id));
      mirrorUnpark(token, held.order.orderId);
      setDeposit(null);
      setOrder(held.order);
      setTable(held.table);
      setDiscount(held.discount);
      setGuest(held.guest);
      /*
       * Признак закрытого счёта возвращаем вместе с заказом.
       *
       * Официант напечатал счёт, отложил стол, пошёл к другим гостям —
       * и вернулся, потому что этот попросил ещё чаю. Без признака
       * кнопка «Вернуть счёт в работу» не показывалась, хотя ровно
       * для этого случая и сделана.
       */
      setPrecheckAt(held.billedAt ?? null);
      return;
    }
    // Стол ещё не выбран — просто ставим: набрать блюда и потом указать
    // стол это нормальный порядок, официант держит поднос и вспоминает номер
    if (table && liveCount > 0 && table.id !== t.id) {
      // А вот СМЕНА стола при непустом заказе — вопрос. Блюда набраны
      // для тех гостей, и молча перенести их на других нельзя:
      // так пробиваются чужие заказы и путается выручка официантов
      const answer = window.confirm(
        `В чеке ${liveCount} поз. на стол ${table.name}.\n\n`
        + `ОК — перенести их на стол ${t.name} (ошиблись столом).\n`
        + `Отмена — отложить на ${table.name} и начать новый заказ.`,
      );
      // parkCurrent сам открывает чистый заказ и сбрасывает скидку с гостем
      if (!answer) parkCurrent();
      /*
       * При «ОК» заказ едет с официантом — но серверный след на
       * покинутом столе обязан исчезнуть. Иначе каждый пройденный
       * стол хранит фантомную копию чека, и «Горилла» расползается
       * по залу (Дым, 11.08). Заказ в руках — сервер не держит тень
       */
      if (answer) mirrorUnpark(token, order.orderId);
    }

    setTable(t);
    setDeposit(resv ? { reservationId: resv.id, amount: resv.deposit, guestName: resv.guestName } : null);

    // Правила места: у бара сбора нет, у столов «шеф» своя скидка.
    // Берём из карты зала — она уже загружена и обновляется вместе с меню
    void fetch(`${BASE}/hall/map`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => (r.ok ? r.json() : null))
      .then((map: {
        servicePct?: number | null; discountPct?: number | null;
        tables: { id: string; servicePct?: number | null; discountPct?: number | null }[];
      }[] | null) => {
        if (!map) { setTableRules(null); return; }
        for (const hall of map) {
          const found = hall.tables.find((x) => x.id === t.id);
          if (found) {
            setTableRules({
              servicePct: found.servicePct ?? null,
              discountPct: found.discountPct ?? null,
              hallServicePct: hall.servicePct ?? null,
              hallDiscountPct: hall.discountPct ?? null,
            });
            return;
          }
        }
        setTableRules(null);
      })
      .catch(() => setTableRules(null));
  };

  /** Стол, открытый другой кассой: забираем через сервер.
   *  Номер выдаём СВОЙ (нумерация — per смена этой кассы),
   *  гость восстанавливается повторным lookup по телефону. */
  const takeRemote = async (row: import('@dastarhan/contracts').OpenOrderRow) => {
    if (!shiftId) return;

    /**
     * Чужой стол — только старшему.
     *
     * Проверка стоит ЗДЕСЬ, а не в окне выбора: взять стол можно
     * тремя путями — из списка открытых, из выбора стола и со схемы
     * зала. Ставить заслон в каждом окне значит однажды забыть про одно.
     *
     * Официант видит чужие столы (смены пересекаются, гость остаётся,
     * когда официант ушёл домой), но войти в них не может: там чужие
     * гости, чужая выручка и чужая ответственность.
     */
    /**
     * Стол со счётом принадлежит кассиру, а не официанту.
     *
     * Официант закрыл счёт: гость получил бумагу с суммой и идёт
     * платить. Открыть такой стол и что-то дописать — значит подменить
     * то, что гость уже держит в руках. Дальше с ним работает тот,
     * кто принимает деньги.
     *
     * Право на оплату здесь верная мерка: кому доверены деньги,
     * тому и стол на этой стадии.
     */
    if (row.awaitingPay && policyOf(perms, 'order.pay') !== 'allowed') {
      alert(
        `Счёт по столу ${row.tableName} уже закрыт.\n\n`
        + 'Гость получил его на руки — открыть стол может только кассир.',
      );
      setPickingTable(false);
      return;
    }

    const mine = row.openedById === user.id;
    // Доступ к чужим столам — ОТДЕЛЬНОЕ право. Раньше он прикрывался
    // правом «снимать позиции», и менеджер получал чужие столы молча
    const senior = policyOf(perms, 'table.any') === 'allowed';
    if (!mine && !senior) {
      alert(
        `Стол ${row.tableName} ведёт ${row.openedByName}.\n\n`
        + 'Позовите его или старшего — у них есть доступ.',
      );
      setPickingTable(false);
      return;
    }

    /*
     * Набранное не должно быть препятствием и здесь. Есть свой стол —
     * откладываем на него; нет — донесём набранное гостям чужого
     * стола, который сейчас берём в работу
     */
    const carry = liveCount > 0 && !table
      ? order.items.filter((i) => !i.removed)
      : [];
    if (liveCount > 0 && table) parkCurrent();
    try {
      await api.openOrderTake(token, row.orderId);
    } catch {
      alert(`Стол ${row.tableName} уже забрала другая касса.`);
      setPickingTable(false);
      return;
    }
    const restored: OrderState = {
      orderId: row.orderId,
      openedBy: { id: row.openedById, name: row.openedByName },
      number: peekNumber(shiftId),
      mode: 'DINE_IN',
      openedAt: row.openedAt,
      // «На кухне» — построчно из контракта; со старого сервера —
      // общим флагом заказа; в крайнем случае — из памяти планшета.
      // Иначе подхваченный стол выглядел свежим и ехал на кухню дважды
      items: (() => {
        const firedLocal = firedIdsOf(row.orderId);
        const restored = row.items.map((i) => ({
          ...i,
          removed: false,
          fired: i.fired ?? (row.fired ? true : firedLocal.has(i.itemId)),
        }));
        // то, что официант набрал по дороге к столу
        return carry.length ? [...restored, ...carry] : restored;
      })(),
    };
    setOrder(restored);
    setTable({ id: row.tableId, name: row.tableName });
    setDiscount(row.discount);
    setGuest(null);

    /*
     * Восстанавливаем признак закрытого счёта.
     *
     * Он живёт в памяти кассы, а кассир открывает ЧУЖОЙ стол — его
     * касса про печать счёта ничего не знает. Без этого кнопка
     * «Вернуть счёт в работу» не появлялась именно там, где нужна:
     * у кассира, к которому гость подошёл передумать.
     *
     * Отметка есть на сервере, она и приезжает в строке заказа.
     */
    setPrecheckAt(row.awaitingPay ? Date.now() : null);
    if (carry.length) setToast(`Добавлено к заказу стола ${row.tableName}`);
    setTakenTable(true);
    setPickingTable(false);
    setOpenTables(false);

    if (row.guestPhone && navigator.onLine) {
      void api.guestLookup(token, { phone: row.guestPhone })
        .then(setGuest)
        .catch(() => undefined);
    }

    /*
     * Красный стол открывается СРАЗУ на оплате.
     *
     * Счёт закрыт, гость держит бумагу и ждёт с деньгами в руке.
     * Показывать кассиру экран набора блюд в этот момент — значит
     * заставить его сделать лишнее касание и дать возможность
     * случайно тронуть состав, который менять уже нельзя.
     *
     * Экран заказа остаётся доступен: из оплаты есть выход назад,
     * и там кассир увидит состав, если гость спорит.
     */
    if (row.awaitingPay) {
      // Ждём кадр: заказ должен встать в состояние до открытия оплаты,
      // иначе экран расчёта увидит пустой чек
      setTimeout(() => openPayRef.current(), 0);
    }
  };
  const [paying, setPaying] = useState(false);
  const [closingShift, setClosingShift] = useState(false);
  const [shiftSheet, setShiftSheet] = useState(false);
  // Экранная лента: виртуальному принтеру сырые байты не по зубам —
  // отчёт показывается на экране, печать документом — по кнопке
  const [tapeView, setTapeView] = useState<string[] | null>(null);
  /*
   * Слушатель ленты живёт СРЕДИ ХУКОВ, выше всех ранних выходов:
   * ниже него был экран «Загружаем меню», и React падал на смене
   * числа хуков между рендерами — чёрный экран после загрузки
   */
  useEffect(() => {
    const h = (e: Event) => setTapeView((e as CustomEvent<string[]>).detail);
    window.addEventListener('dastarhan:tape', h);
    return () => window.removeEventListener('dastarhan:tape', h);
  }, []);
  useEffect(() => {
    const h = () => setShiftSheet(true);
    window.addEventListener('dastarhan:open-shift-sheet', h);
    return () => window.removeEventListener('dastarhan:open-shift-sheet', h);
  }, []);
  /*
   * Правда о смене живёт на сервере: офис мог закрыть её или
   * разделить по границе 06:00 — касса обязана подхватить, а не
   * упрямиться локальной памятью. Раз в 45 секунд и при возврате
   * на вкладку сверяемся; расхождение — принимаем серверную:
   * шапка, страж и счётчики оживают сами, без переустановок
   */
  useEffect(() => {
    if (!token) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await api.shiftCurrent(token);
        if (stop) return;
        const localId = localStorage.getItem(LS.shiftId);
        if (!r.open) {
          if (localId) {
            localStorage.removeItem(LS.shiftId);
            localStorage.removeItem('dastarhan.shiftOpenedAt');
            setShiftId(null);
          }
          return;
        }
        if (r.id !== localId) {
          localStorage.setItem(LS.shiftId, r.id);
          localStorage.setItem('dastarhan.shiftOpenedAt', r.openedAt);
          localStorage.setItem(`dastarhan.shiftSum.${r.id}`, String(r.revenue));
          setShiftId(r.id);
          setShiftSum(r.revenue);
          setShiftNo(null);
        }
      } catch { /* офлайн — живём локальной памятью */ }
    };
    void tick();
    const iv = window.setInterval(() => void tick(), 45_000);
    const onVis = () => { if (!document.hidden) void tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop = true;
      window.clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [token]);
  // номер смены — приезжает с первым отчётом, живёт для шапки
  const [shiftNo, setShiftNo] = useState<number | null>(null);
  const [elevateFor, setElevateFor] = useState<string | null>(null); // itemId
  const [queued, setQueued] = useState(0);

  /*
   * Оболочка Windows спросит это число при закрытии окна: кассир
   * должен знать, что чеки ещё не ушли на сервер. Пишем прямо в окно
   * при каждой перемене — сколько бы раз очередь ни менялась за смену.
   */
  useEffect(() => {
    (window as unknown as { __dastarhanPending?: number }).__dastarhanPending = queued;
  }, [queued]);
  const [online, setOnline] = useState(true);
  // уход в офлайн говорится один раз, а не при каждой проверке
  const wasOnline = useRef(true);
  useEffect(() => {
    if (wasOnline.current && !online) sayRef.current('Связь пропала — чеки копятся в очереди');
    wasOnline.current = online;
  }, [online]);

  // Esc закрывает верхний диалог кассы — тем же путём, что кнопка «Отмена».
  // ОДИН список на ВСЕ диалоги: раньше половина окон на Esc не отвечала,
  // и кассир не мог предсказать, что сделает клавиша
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (keysHelp) setKeysHelp(false);
      else if (paid) setPaid(null);
      else if (splitting) setSplitting(false);
      else if (pickingGuest) setPickingGuest(false);
      else if (discountFlow) { setDiscountFlow(null); setPendingDiscount(null); }
      else if (noteFor) setNoteFor(null);
      else if (promoPrompt) setPromoPrompt(false);
      else if (voidFlow) setVoidFlow(null);
      else if (reassignOpen) setReassignOpen(false);
      else if (billOpen) setBillOpen(false);
      else if (payElevate) setPayElevate(false);
      else if (cardOpen) setCardOpen(false);
      else if (weighing) setWeighing(null);
      else if (elevateFor) setElevateFor(null);
      else if (transferOpen) setTransferOpen(false);
      else if (stopOpen) setStopOpen(false);
      else if (showMap) setShowMap(false);
      else if (openTables) setOpenTables(false);
      else if (pickingTable) setPickingTable(false);
      else if (printerOpen) setPrinterOpen(false);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [keysHelp, paid, splitting, pickingGuest, discountFlow, cardOpen, weighing,
    elevateFor, voidFlow, reassignOpen, noteFor, promoPrompt, billOpen, payElevate, transferOpen, stopOpen, showMap, openTables, pickingTable, printerOpen]);

  const calls = useQuery({

    queryKey: ['calls'],

    queryFn: async () => {

      const res = await fetch(`${BASE}/hall/calls`, { headers: { Authorization: `Bearer ${token}` } });

      if (!res.ok) return { rows: [] as { id: string; tableName: string; kind: 'WAITER' | 'BILL'; at: string }[] };

      return (await res.json()) as { rows: { id: string; tableName: string; kind: 'WAITER' | 'BILL'; at: string }[] };

    },

    refetchInterval: 15_000,

  });

  // Очередь растёт в офлайне — тихий низкий бип: не тревога, напоминание
  const prevQueued = useRef<number | null>(null);
  useEffect(() => {
    if (prevQueued.current !== null && queued > prevQueued.current && audioCtx.current) {
      const ctx = audioCtx.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 440;
      gain.gain.value = 0.04;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.09);
    }
    prevQueued.current = queued;
  }, [queued]);

  // Звук новой полоски: касса в шуме зала — глаза заняты, уши свободны
  const seenCalls = useRef<Set<string>>(new Set());
  const audioCtx = useRef<AudioContext | null>(null);
  useEffect(() => {
    const unlock = () => {
      if (!audioCtx.current) audioCtx.current = new AudioContext();
      void audioCtx.current.resume();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);
  useEffect(() => {
    const rows = calls.data?.rows ?? [];
    const fresh = rows.filter((c) => !seenCalls.current.has(c.id));
    if (fresh.length > 0 && seenCalls.current.size > 0 && audioCtx.current) {
      const ctx = audioCtx.current;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.12, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.connect(g).connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.36);
    }
    for (const c of rows) seenCalls.current.add(c.id);
  }, [calls.data]);

  const ackCall = (id: string) => {

    void fetch(`${BASE}/hall/calls/${id}`, {

      method: 'PATCH', headers: { Authorization: `Bearer ${token}` },

    }).then(() => calls.refetch());

  };


  const catalog = useQuery({
    queryKey: ['catalog'],
    /*
     * Касса обязана включаться и без интернета: меню — из last known
     * good копии. Успех освежает кэш; ошибка сети поднимает кассу из
     * кэша, а фоновые повторы сами подтянут свежее, когда связь
     * вернётся. «Меню не загрузилось» остаётся только для самого
     * первого запуска в жизни кассы
     */
    queryFn: async () => {
      try {
        const fresh = await api.catalog(token);
        try { localStorage.setItem('dastarhan.catalog.cache', JSON.stringify(fresh)); } catch { /* тесно */ }
        return fresh;
      } catch (e) {
        const raw = localStorage.getItem('dastarhan.catalog.cache');
        if (raw) return CatalogResponse.parse(JSON.parse(raw));
        throw e;
      }
    },
    // стоп, поставленный в офисе, должен доехать до кассы сам
    refetchInterval: 60_000,
  });

  // Признаки вертикали нужны хукам ниже, поэтому вычисляем их здесь —
  // до ранних выходов «загружаем меню» и «меню не загрузилось»
  const features = {
    kitchen: catalog.data?.features?.kitchen ?? true,
    tables: catalog.data?.features?.tables ?? true,
    courses: catalog.data?.features?.courses ?? false,
  };

  /**
   * Отправить на кухню, не закрывая счёт.
   *
   * В ресторане это главное действие вечера: гость заказал — кухня
   * начала готовить, а платят через час. Раньше бегунок печатался
   * только при оплате, то есть повар узнавал о заказе последним.
   *
   * Отправленные позиции помечаются, чтобы второе нажатие не послало
   * их снова: повар не должен готовить дважды.
   */
  // Признак отправки живёт В ПОЗИЦИИ (item.fired): отдельный список
  // умирал вместе с перезагрузкой страницы, а блюдо на плите оставалось
  const senior = policyOf(perms, 'order.item.remove') === 'allowed';
  // Право набирать заказ: без него человек видит меню, но не может
  // ничего добавить. Так бывает у роли «Только просмотр» — редкой,
  // но нужной: стажёр смотрит, как работает касса
  const canOrder = policyOf(perms, 'order.create') !== 'denied';
  const fireToKitchen = useCallback(async () => {
    const fresh = order.items.filter((i) => !i.removed && !i.fired);
    if (fresh.length === 0) { setToast('Всё уже отправлено'); return; }

    // Событие для кухонного экрана — тем же каналом, что и остальное
    await enqueue({
      eventId: ulid(), terminalId: localStorage.getItem(LS.terminalId) ?? 'pos',
      at: new Date().toISOString(), type: 'order.fired',
      orderId: order.orderId, tableId: table?.id ?? null, tableName: table?.name ?? null,
      openedByName: user.fullName,
      items: fresh.map((i) => ({
        itemId: i.itemId, productId: i.productId, name: i.name,
        qty: i.qty, unitPrice: i.unitPrice, modifiers: i.modifiers,
        ...(i.course !== undefined ? { course: i.course } : {}),
      })),
    } as never);
    void flush(token).then(setQueued);

    // Бумажный бегунок: KDS может быть выключен, а повар работать должен
    const kUrl = localStorage.getItem('dastarhan.printer.kitchen') ?? '';
    const bUrl = localStorage.getItem('dastarhan.printer.bar') ?? '';
    // в exe URL не нужны: бегунок уходит через мост на принтер цеха
    if (features.kitchen && (isDesktopApp() || kUrl || bUrl)) {
      // Цех с наследованием (stationOf): у подгруппы пусто — берём
      // у группы. Иначе «Напитки» внутри «Бара» летели бы на кухню,
      // хотя цех задан у самого «Бара»
      const catsAll = catalog.data?.categories ?? [];
      const byIdC = new Map(catsAll.map((c) => [c.id, c]));
      const catBy = new Map(catsAll.map((c) => [
        c.id, stationOf(c, c.parentId ? byIdC.get(c.parentId) ?? null : null),
      ]));
      const prodCat = new Map((catalog.data?.products ?? []).map((pp) => [pp.id, pp.categoryId]));
      const groups = new Map<string, string[]>();
      for (const it of fresh) {
        const st = (it.productId && catBy.get(prodCat.get(it.productId) ?? '')) || 'kitchen';
        const arr = groups.get(st) ?? [];
        arr.push(`${it.qty} × ${it.name}`
          + (it.modifiers.length ? `  (${it.modifiers.map((mm) => mm.name).join(', ')})` : '')
          + (it.course !== undefined && it.course !== 1
            ? `  [${it.course === 0 ? 'СРОЧНО' : `подача ${it.course}`}]` : ''));
        groups.set(st, arr);
      }
      for (const [st, lines] of groups) {
        const url = st === 'bar' ? (bUrl || kUrl) : kUrl;
        if (!url && !isDesktopApp()) continue;
        void printRunner([
          `*** ${st === 'bar' ? 'БАР' : 'КУХНЯ'} ***`,
          table ? `${tt('table')} ${table.name}` : 'На вынос',
          user.fullName,
          new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
          '',
          ...lines,
        ], url, st);
      }
    }

    // Кухня начала готовить — стол больше не освободить самому:
    // блюда уже в работе, и бросить их нельзя
    if (table && shiftId) {
      void api.openOrderPut(token, {
        orderId: order.orderId, tableId: table.id, tableName: table.name,
        number: order.number, openedAt: order.openedAt,
        items: order.items.filter((i) => !i.removed).map((i) => ({
          itemId: i.itemId, productId: i.productId, name: i.name,
          qty: i.qty, unitPrice: i.unitPrice, modifiers: i.modifiers,
          // fresh — те, что уходят этим нажатием; остальные уже были
          ...(i.fired || fresh.some((f) => f.itemId === i.itemId) ? { fired: true } : {}),
        })),
        discount, fired: true,
      }).catch(() => undefined);
    }

    setOrder((o) => reduceOrder(o, {
      type: 'items.fired', itemIds: fresh.map((i) => i.itemId),
    }));
    rememberFired(order.orderId, fresh.map((i) => i.itemId));
    setToast(`Напечатано: ${fresh.length} поз.`);
    // В официантском укладе НЕ уходим: «Заказать» значит «стол остаётся
    // открытым», для выхода есть отдельная кнопка рядом. Автопереход
    // здесь перекрывал экран, и «Закрыть счёт» становилась недоступной —
    // официант видел кнопку, но она была под списком столов
    if (!waiter && afterOrder === 'list') setOpenTables(true);
  }, [order, table, user, token, features.kitchen, catalog.data, waiter, afterOrder]);

  /**
   * Забытый набор освобождает стол.
   *
   * Официант выбрал стол, потыкал блюда и ушёл — а стол занят, и другой
   * его не возьмёт. Через двадцать минут без касаний набранное очищается,
   * стол освобождается.
   *
   * НО только если на кухню ничего не ушло. Если повар уже готовит,
   * бросать стол нельзя ни при каких условиях: блюда придут, а нести
   * их будет некуда. Такой стол держится до оплаты или до решения
   * человека — старший всегда может войти в него через «Открытые столы».
   */
  useEffect(() => {
    if (liveCount === 0 || hasFired(order.items)) return;
    let timer: number;
    const reset = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        setOrder(newOrder(localStorage.getItem(LS.shiftId) ?? 'draft'));
        setTable(null);
        setDiscount(0);
        setGuest(null);
        setToast('Набор очищен: 20 минут без работы');
      }, 20 * 60_000);
    };
    const events = ['pointerdown', 'keydown', 'touchstart'] as const;
    for (const e of events) window.addEventListener(e, reset, { passive: true });
    reset();
    return () => {
      window.clearTimeout(timer);
      for (const e of events) window.removeEventListener(e, reset);
    };
  }, [liveCount, order.items]);

  // Магазину и салону рассадка не нужна: «в зале» тихо превращается
  // в «с собой», кассир вопросов не видит.
  // ХУК СТОИТ ЗДЕСЬ, а не ниже: после него в теле есть ранние return
  // (загрузка, ошибка каталога), и хук за ними ломает порядок хуков
  // между рендерами — React отвечает белым экраном (ошибка #310)
  useEffect(() => {
    if (catalog.data && !catalog.data.features.tables && order.mode === 'DINE_IN') {
      setOrder((o) => reduceOrder(o, { type: 'mode', mode: 'TAKEAWAY' }));
    }
  }, [catalog.data, order.mode]);


  // Новый стоп — новость для кассира: он как раз собирался это пробить
  // Стоп-лист меняется не только появлением и уходом позиций:
  // «осталось 2» → «закончилось» — тоже новость для того, кто продаёт
  const knownStops = useRef<Map<string, number | null> | null>(null);
  useEffect(() => {
    const list = catalog.data?.stopList;
    if (!list) return;
    const now = new Map<string, number | null>(Object.entries(list));
    const prev = knownStops.current;
    if (prev) {
      const nameOf = (id: string) =>
        catalog.data?.products.find((p) => p.id === id)?.name ?? '';
      const fresh: string[] = [];
      const gone: string[] = [];
      const ranOut: string[] = [];
      for (const [id, left] of now) {
        if (!prev.has(id)) {
          fresh.push(typeof left === 'number' && left > 0
            ? `${nameOf(id)} — осталось ${left}`
            : nameOf(id));
        } else {
          const was = prev.get(id) ?? null;
          const isNumWas = typeof was === 'number' && was > 0;
          const isNumNow = typeof left === 'number' && left > 0;
          if (isNumWas && !isNumNow) ranOut.push(nameOf(id));
        }
      }
      for (const id of prev.keys()) if (!now.has(id)) gone.push(nameOf(id));
      const clean = (a: string[]) => a.filter(Boolean);
      if (clean(fresh).length > 0) sayRef.current(`Стоп: ${clean(fresh).join(', ')}`);
      if (clean(ranOut).length > 0) sayRef.current(`Закончилось совсем: ${clean(ranOut).join(', ')}`);
      if (clean(gone).length > 0) sayRef.current(`Снова в продаже: ${clean(gone).join(', ')}`);
    }
    knownStops.current = now;
  }, [catalog.data]);

  /*
   * Досыл зала: все локально отложенные столы заново зеркалим на
   * сервер. Операция идемпотентна — сервер кладёт снапшот по
   * orderId, повтор ничего не портит
   */
  const resyncParkedRef = useRef<() => void>(() => {});
  resyncParkedRef.current = () => {
    for (const entry of parked) mirrorPark(token, entry);
  };

  const push = useCallback(async (event: SyncEvent) => {
    await enqueue(event);
    // «Связь есть» и «очередь пуста» — разные вещи. Одно застрявшее
    // событие не должно объявлять кассу офлайном: кассир начнёт искать
    // поломку сети, которой нет
    const { left, reached } = await flushEx(token);
    setQueued(left);
    setOnline(reached);
  }, [token]);

  // Досылаем накопленное при старте и при возвращении сети
  useEffect(() => {
    void pendingCount().then(setQueued);
    /*
     * Досылаем зал на старте — безусловно.
     *
     * Касса могла быть перезапущена без связи, отложить столы «в
     * тетрадку» и подняться уже с интернетом: события «сеть
     * появилась» при этом не будет, а пустая очередь не считается
     * успешным контактом. Само зеркалирование идемпотентно и в
     * офлайне тихо не удастся — пробовать безопасно
     */
    resyncParkedRef.current();
    void flushEx(token).then(({ left, reached }) => {
      setQueued(left);
      setOnline(reached);
    });
    const onUp = () => {
      // сколько ждало отправки ДО досыла — иначе цифра всегда 0
      void pendingCount().then((before) => {
        void flushEx(token).then(({ left, reached }) => {
          setQueued(left);
          setOnline(reached);
          localStorage.setItem('dastarhan.lastSync', new Date().toISOString());
          const sent = Math.max(0, before - left);
          /*
           * Столы, отложенные без связи, сервер не видел вовсе:
           * зеркалирование молча падало. Пока касса одна — не беда,
           * но кухня, вторая касса и офис слепы, а после смены такой
           * стол некому закрыть. Возвращается связь — досылаем зал
           */
          if (reached) resyncParkedRef.current();
          if (sent > 0) sayRef.current(`Связь вернулась — ушло чеков: ${sent}`);
          else if (left === 0) sayRef.current('Связь вернулась');
        });
      });
    };
    window.addEventListener('online', onUp);

    /*
     * Пробуем отправить очередь каждые полминуты, а не только когда
     * система скажет «сеть появилась».
     *
     * Событие online означает лишь, что найден сетевой интерфейс.
     * В заведении это случается постоянно: Wi-Fi поднялся, а роутер
     * ещё не достучался до провайдера; сервер перезагружается; кабель
     * дёрнули и вернули. Событие уже прошло, а чеки остались лежать —
     * и уйдут только при следующем обрыве связи, то есть неизвестно
     * когда.
     *
     * Тихая попытка раз в тридцать секунд стоит одного запроса
     * и снимает целый класс потерянных чеков.
     */
    const beat = setInterval(() => {
      if (!navigator.onLine) return;
      void pendingCount().then((n) => {
        if (n > 0) onUp();
      });
    }, 30_000);

    return () => {
      window.removeEventListener('online', onUp);
      clearInterval(beat);
    };
  }, [token]);

  if (!shiftId) {
    return (
      <ShiftGate waiter={waiter} cashier={user.fullName} token={token}
        onContinue={(id) => {
          // Продолжаем существующую смену: её id становится нашим,
          // и все чеки лягут в неё. Нового события не шлём —
          // смена уже открыта, второе только запутает
          localStorage.setItem(LS.shiftId, id);
          setShiftId(id);
          setOrder(newOrder(id));
        }}
        onOpen={(openingCash) => {
          // Смену открывает тот, кто отвечает за кассу: в ней размен,
          // и вечером сходиться будет с него
          if (policyOf(perms, 'cash.shift.open') === 'denied') {
            alert('Смену открывает старший');
            return;
          }
          const id = ulid();
          localStorage.setItem(LS.shiftId, id);
          localStorage.setItem('dastarhan.shiftOpenedAt', new Date().toISOString());
          setShiftId(id);
          setOrder(newOrder(id));
          void push({
            eventId: ulid(), terminalId, at: new Date().toISOString(),
            type: 'shift.opened', shiftId: id, openedBy: user.id, openingCash,
          });
        }}
        onLogout={onLogout}
      />
    );
  }

  if (catalog.isPending) {
    return <div className="gate"><span className="eyebrow">Загружаем меню…</span></div>;
  }
  if (catalog.isError) {
    return (
      <div className="gate">
        <h1>Меню не загрузилось</h1>
        <p className="hint">Проверьте связь с сервером и попробуйте ещё раз.</p>
        <button className="btn accent" onClick={() => catalog.refetch()}>Повторить</button>
        <button className="btn ghost" onClick={onLogout}>Выйти</button>
      </div>
    );
  }


  const promo = applyPromos(order.items, catalog.data?.promos ?? [], new Date());
  // Лояльность идёт тем же каналом, что акции: одна строка в чеке,
  // один снапшот в отчёте, ноль особых случаев в материализации
  const rawSubtotal = orderTotals(order).subtotal;
  const loyaltyAmount = guest && guest.percent > 0
    ? Math.round((rawSubtotal * guest.percent) / 100) : 0;

  // Карта идёт тем же каналом, что акции и лояльность: одна строка
  // в чеке, один снапшот в отчёте, ноль особых случаев при закрытии.
  // Если у гостя есть и карта, и свой процент — берём БОЛЬШИЙ:
  // две скидки на один счёт гость не покупал
  const cardAmount = card && card.percent > 0
    ? Math.round((rawSubtotal * card.percent) / 100) : 0;
  const useCard = cardAmount > loyaltyAmount;
  const allPromos = [
    ...promo.applied,
    ...(useCard
      ? [{ name: `${card!.label} ${card!.percent}%`, amount: cardAmount }]
      : loyaltyAmount > 0
        ? [{ name: `Лояльность ${guest!.percent}%`, amount: loyaltyAmount }]
        : []),
  ];
  // Скидка в оплате показывается с происхождением: кассир видит,
  // ОТКУДА взялась каждая строка (акция · карта · лояльность)
  // и как её убрать. В чек и событие уходит прежний allPromos —
  // сервер и лента не меняются
  const promoRows = [
    ...promo.applied.map((p) => ({ ...p, kind: 'promo' as const })),
    ...(useCard
      ? [{ name: `${card!.label} ${card!.percent}%`, amount: cardAmount, kind: 'card' as const }]
      : loyaltyAmount > 0
        ? [{ name: `Лояльность ${guest!.percent}%`, amount: loyaltyAmount, kind: 'loyalty' as const }]
        : []),
  ];
  // Сбор за обслуживание: правило приходит с каталогом, решение
  // о применении — здесь, в одном месте на весь экран
  // Правило стола важнее общего: в баре сбора нет, у столов «шеф»
  // своя скидка. Загружаем вместе со схемой зала — она и так нужна
  // Запрет скидок группы: на алкоголь скидки часто нельзя, и помнить
  // об этом в час пик невозможно. Хватает одной такой позиции в чеке —
  // скидка не даётся на весь счёт, потому что делить её построчно
  // кассир не станет, а гость увидит несходящуюся сумму
  const noDiscountCats = new Set(
    (catalog.data?.categories ?? [])
      .filter((c) => c.allowDiscount === false)
      .map((c) => c.id),
  );
  const prodCat = new Map((catalog.data?.products ?? []).map((p2) => [p2.id, p2.categoryId]));
  const hasNoDiscountItems = order.items.some(
    (i) => !i.removed && i.productId && noDiscountCats.has(prodCat.get(i.productId) ?? ''),
  );

  const svcCfg = catalog.data?.service;
  const svcPct = svcCfg && serviceApplies(
    { percent: svcCfg.percent, dineInOnly: svcCfg.dineInOnly, fromGuests: svcCfg.fromGuests },
    order.mode,
    // Числа гостей касса не спрашивает: официант и так занят, а порог
    // «от N гостей» настраивают редкие заведения. Считаем как одного —
    // порог 0 (по умолчанию) при этом работает верно
    1,
  ) ? svcCfg.percent : 0;

  // Стол и зал могут переопределить: 0 значит «здесь не берём»,
  // а не «не задано» — разница принципиальная
  const placeRules = tableRules
    ? resolveRules(
        { servicePct: tableRules.servicePct ?? null, discountPct: tableRules.discountPct ?? null },
        { servicePct: tableRules.hallServicePct ?? null, discountPct: tableRules.hallDiscountPct ?? null },
        svcPct,
      )
    : { servicePct: svcPct, discountPct: 0, source: 'account' as const };
  // Скидка стола идёт наравне с картой и ручной: берём наибольшую,
  // а не сумму — три раза по десять процентов это треть счёта,
  // чего никто не обещал ни в одном из трёх случаев
  const placeDiscount = placeRules.discountPct > 0
    ? Math.round((orderTotals(order).subtotal * placeRules.discountPct) / 100)
    : 0;
  const t = orderTotals(
    order,
    // Карта и лояльность не складываются — берём большую из двух:
    // две скидки на один счёт гость не покупал
    Math.max(discount, placeDiscount) + promo.discount + Math.max(loyaltyAmount, cardAmount),
    placeRules.servicePct,
  );


  const payPolicy = policyOf(perms, 'order.pay');
  const openPay = () => {
    if (!order.items.some((i) => !i.removed)) return;
    if (payPolicy === 'denied') {
      say('Оплату принимает кассир. Отложите стол или позовите его');
      return;
    }
    if (payPolicy === 'elevated_pin' && !payApproved) {
      // Не отказ, а развилка: официант зовёт кассира, тот вводит PIN.
      // Отложить стол он может сам — тогда чек закроет кассир со стойки
      setPayElevate(true);
      return;
    }
    setPaying(true);
  };
  openPayRef.current = openPay;

  const printTape = async (lines: string[]) => {
    if (await isVirtualPrinter()) setTapeView(lines);
    else await printSmart(lines);
  };

  if (tapeView) {
    return (
      <div className="tape-view" role="dialog" aria-modal="true" aria-label="Лента">
        <div className="tape-paper"><pre>{tapeView.join('\n')}</pre></div>
        <div className="tape-actions">
          <button className="btn" onClick={() => {
            printReceipt(tapeView);
          }}>🖨 Печатать документом</button>
          <button className="btn ghost" title="Сырые байты на чековый принтер"
            onClick={() => { void printSmart(tapeView); }}>🖨 Сырым (чековый)</button>
          <button className="btn accent" onClick={() => setTapeView(null)}>Закрыть</button>
        </div>
      </div>
    );
  }

  if (shiftSheet) {
    return (
      <ShiftSheet
        shiftNumber={shiftNo}
        openedAt={localStorage.getItem('dastarhan.shiftOpenedAt')}
        cashier={user.fullName}
        /* Прошлые смены — тем, кто вправе закрывать смену: кто
           подписывает Z, тот и копии смотрит. Отдельного «старшего»
           звания для этого не требуется */
        senior={policyOf(perms, 'cash.shift.close') !== 'denied'}
        online={online}
        loadRecent={() => api.shiftsRecent(token)}
        onClose={() => setShiftSheet(false)}
        onCloseShift={() => { setShiftSheet(false); setClosingShift(true); }}
        onDayX={(d) => void (async () => {
          try {
            const r = await api.dayReport(token, d);
            await printTape(xReportLines(r,
              localStorage.getItem(LS.place) ?? 'Dastarhan', paperWidth()));
          } catch { alert('Нет связи: отчёт за день требует сервера.'); }
        })()}
        onDayAct={(d) => void (async () => {
          try {
            const r = await api.dayReport(token, d);
            await printTape(actLines(r,
              localStorage.getItem(LS.place) ?? 'Dastarhan', paperWidth()));
          } catch { alert('Нет связи: акт за день требует сервера.'); }
        })()}
        onX={() => void (async () => {
          /*
           * Верхний X — «за сегодня»: операционный день от 06:00,
           * даже если вчерашнюю смену забыли закрыть. Продажи дня
           * начинаются с нуля в 06:00; ящик при слитой смене лента
           * честно пометит предупреждением — деньги в железе не
           * умеют обнуляться по расписанию
           */
          try {
            const d = new Date(Date.now() - 6 * 3600_000)
              .toISOString().slice(0, 10);
            const r = await api.dayReport(token, d);
            await printTape(xReportLines(r,
              localStorage.getItem(LS.place) ?? 'Dastarhan', paperWidth()));
          } catch { alert('Нет связи: X-отчёт требует сервера.'); }
        })()}
        onAct={() => void (async () => {
          try {
            const d = new Date(Date.now() - 6 * 3600_000)
              .toISOString().slice(0, 10);
            const r = await api.dayReport(token, d);
            await printTape(actLines(r,
              localStorage.getItem(LS.place) ?? 'Dastarhan', paperWidth()));
          } catch { alert('Нет связи: акт требует сервера.'); }
        })()}
        onPastX={(sh) => void (async () => {
          try {
            const r = await api.shiftReport(token, sh.id);
            await printTape(xReportLines(r,
              localStorage.getItem(LS.place) ?? 'Dastarhan', paperWidth()));
          } catch { alert('Нет связи.'); }
        })()}
        onPastZ={(sh) => void (async () => {
          try {
            const r = await api.shiftReport(token, sh.id);
            await printTape(zReportLines(r,
              localStorage.getItem(LS.place) ?? 'Dastarhan',
              { actualCash: r.expectedCash, copy: true }, paperWidth()));
          } catch { alert('Нет связи.'); }
        })()}
        onPastAct={(sh) => void (async () => {
          try {
            const r = await api.shiftReport(token, sh.id);
            await printTape(actLines(r,
              localStorage.getItem(LS.place) ?? 'Dastarhan', paperWidth()));
          } catch { alert('Нет связи.'); }
        })()}
      />
    );
  }

  if (closingShift) {
    return (
      <ShiftClose token={token} shiftId={shiftId} queued={queued}
        onBack={() => setClosingShift(false)}
        onConfirm={(closingCash) => {
          void push({
            eventId: ulid(), terminalId, at: new Date().toISOString(),
            type: 'shift.closed', shiftId, closingCash,
          });
          localStorage.removeItem(seqKey(shiftId));
          localStorage.removeItem(LS.shiftId);
          setClosingShift(false);
          setShiftId(null);
        }}
      />
    );
  }

  if (viewChecks) {
    return (
      <ChecksScreen token={token} shiftId={shiftId} online={online}
        onBack={() => setViewChecks(false)}
        onCancelOrder={async (orderId, reason, approver, reopen) => {
          await push({
            eventId: ulid(), terminalId, at: new Date().toISOString(),
            type: 'order.cancelled', orderId, reason, approver,
            approverToken: localStorage.getItem('dastarhan.approverToken') ?? undefined,
            ...(reopen ? { reopen: true } : {}),
          });
        }}
      />
    );
  }

  if (paying) {
    const discountPolicy = policyOf(perms, 'order.discount');
    return (
      <>
      <PayScreen items={order.items.filter((i) => !i.removed)}
        order={order}
        table={table}
        placeName={localStorage.getItem(LS.place) ?? 'Дастархан'}
        cashierName={user.fullName}
        totals={{
          subtotal: t.subtotal, discount,
          service: t.service, servicePct: t.servicePct,
        }}
        due={Math.max(0, t.total - (deposit ? Math.min(deposit.amount, t.total) : 0))} number={order.number}
        discount={discount}
        promos={promoRows}
        card={card ? { label: card.label, percent: card.percent, active: useCard } : null}
        onCard={() => setCardOpen(true)}
        onCardClear={() => { setCard(null); say('Карта убрана из чека'); }}
        guest={guest ? { label: guest.name ?? guest.phone, percent: guest.percent, note: guest.note } : null}
        deposit={deposit ? { amount: Math.min(deposit.amount, t.total), guestName: deposit.guestName } : null}
        onDepositClear={() => setDeposit(null)}
        onPromoCode={() => setPromoPrompt(true)}
        canUnbill={policyOf(perms, 'table.unbill') === 'allowed'}
        billed={precheckAt !== null || takenTable}
        onUnbill={() => {
          void (async () => {
            const res = await fetch(`${BASE}/open-orders/${order.orderId}/unbill`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) { alert('Не получилось вернуть счёт'); return; }
            // Стол снова зелёный: снимаем локальные отметки, чтобы касса
            // не считала счёт закрытым до следующего опроса сервера
            setPrecheckAt(null);
            setPrecheckSnap(null);
            setPaying(false);
            say('Стол вернулся официанту — можно дозаказывать');
          })();
        }}
        canSplit={features.tables && order.mode === 'DINE_IN' && !!table && liveCount > 1}
        onSplit={() => setSplitting(true)}
        onGuest={() => {
          // Гость в базе — это скидка и история. Заводить его должен
          // тот, кто отвечает за чистоту базы, иначе она зарастёт
          if (policyOf(perms, 'guest.create') === 'denied') {
            alert('Гостей заводит старший');
            return;
          }
          if (!navigator.onLine) {
            alert('Поиск гостя по телефону требует связи.');
            return;
          }
          setPickingGuest(true);
        }}
        onGuestClear={() => setGuest(null)}
        canDiscount={discountPolicy !== 'denied' && !hasNoDiscountItems}
        noDiscountReason={hasNoDiscountItems
          ? 'В чеке есть товары, на которые скидка запрещена'
          : null}
        onEditDiscount={() => {
          if (discountPolicy === 'elevated_pin') {
            if (!navigator.onLine) {
              alert('Скидку подтверждает менеджер — для проверки PIN нужна связь.');
              return;
            }
            setDiscountFlow('elevate');
          } else {
            setDiscountFlow('dialog');
          }
        }}
        onPrint={() => doPrecheck(false)}
        onBack={() => {
          setPaying(false);
          // Закрытый счёт кассир не редактирует: возвращаем к столам,
          // чтобы он взял следующий, а не завис на чужом заказе
          if (precheckAt !== null || takenTable) {
            const sid = localStorage.getItem(LS.shiftId) ?? 'draft';
            setOrder(newOrder(sid));
            setTable(null);
            setDiscount(0);
            setGuest(null);
            setTakenTable(false);
            setPrecheckAt(null);
            setOpenTables(true);
          }
        }}
        onConfirm={(payments: PaymentDraft[]) => {
          bumpShiftSum(t.total);
          // оплата принята (офлайн-first: очередь = успех) — мажор 660→880
          if (audioCtx.current) {
            const ctx = audioCtx.current;
            [[660, 0], [880, 0.09]].forEach(([f, at]) => {
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.frequency.value = f!;
              gain.gain.value = 0.05;
              osc.connect(gain).connect(ctx.destination);
              osc.start(ctx.currentTime + at!);
              osc.stop(ctx.currentTime + at! + 0.08);
            });
          }
          const live = order.items.filter((i) => !i.removed);
          // «Приведи друга»: каждые N визитов гость уносит код для друга.
          // Генерирует касса (офлайн-дружелюбно), сервер зафиксирует из события.
          const referralGift = (guest && referralCfg
            && (guest.visits + 1) % referralCfg.everyVisits === 0)
            ? {
                code: Array.from({ length: 6 }, () =>
                  'ABCDEFGHJKMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 31)]).join(''),
                percent: referralCfg.percent,
              }
            : null;

          void push({
            eventId: ulid(), terminalId, at: new Date().toISOString(),
            type: 'order.closed',
            ...(order.comment ? { comment: order.comment } : {}),
            // подпись менеджера на скидку, если она была подтверждена PIN
            discountToken: localStorage.getItem('dastarhan.approverToken') ?? undefined,
            // списанные бонусы: сервер проведёт их и начислит кэшбэк с остатка
            spendBonus: 0,
            orderId: order.orderId, shiftId, number: order.number, mode: order.mode,
            cashierId: user.id,
            ...(order.mode === 'DINE_IN' && table
              ? { tableId: table.id, tableName: table.name } : {}),
            openedAt: order.openedAt,
            items: live.map((i) => ({
              itemId: i.itemId, productId: i.productId, name: i.name,
              qty: i.qty, unitPrice: i.unitPrice, modifiers: i.modifiers,
              ...(i.course !== undefined ? { course: i.course } : {}),
            })),
            payments,
            discount: t.discount,
            // Обслуживание входит в сумму, которую платит гость:
            // без него сервер не сойдётся и отвергнет чек
            ...(t.service > 0 ? { service: t.service, servicePct: t.servicePct } : {}),
            promos: allPromos,
            ...(guest ? { guestId: guest.guestId, guestPhone: guest.phone } : {}),
            ...(deposit && deposit.amount > 0 ? {
              deposit: {
                reservationId: deposit.reservationId,
                amount: Math.min(deposit.amount, t.total),
              },
            } : {}),
            ...(promoApplied ? { promoCode: promoApplied } : {}),
            ...(referralGift ? { referralCode: referralGift } : {}),
            openedById: order.openedBy?.id ?? user.id,
            openedByName: order.openedBy?.name ?? user.fullName,
          });
          // Бегунки по цехам: бумажная страховка KDS — если задан URL
          // принтера цеха, туда уезжает своя лента позиций
          const kUrl = localStorage.getItem('dastarhan.printer.kitchen') ?? '';
          const bUrl = localStorage.getItem('dastarhan.printer.bar') ?? '';
          // в рознице (features.kitchen=false) цехов нет — бегунки не нужны
          if (features.kitchen && (isDesktopApp() || kUrl || bUrl)) {
            // Цех с наследованием (stationOf): у подгруппы пусто — берём
      // у группы. Иначе «Напитки» внутри «Бара» летели бы на кухню,
      // хотя цех задан у самого «Бара»
      const catsAll = catalog.data?.categories ?? [];
      const byIdC = new Map(catsAll.map((c) => [c.id, c]));
      const catBy = new Map(catsAll.map((c) => [
        c.id, stationOf(c, c.parentId ? byIdC.get(c.parentId) ?? null : null),
      ]));
            const prodCat = new Map((catalog.data?.products ?? []).map((pp) => [pp.id, pp.categoryId]));
            const groups = new Map<string, string[]>();
            for (const it of order.items.filter((x) => !x.removed)) {
              const st = (it.productId && catBy.get(prodCat.get(it.productId) ?? '')) || 'kitchen';
              const arr = groups.get(st) ?? [];
              arr.push(`${it.qty} × ${it.name}` + (it.modifiers.length
                ? `  (${it.modifiers.map((mm) => mm.name).join(', ')})` : ''));
              groups.set(st, arr);
            }
            for (const [st, lines] of groups) {
              const url = st === 'bar' ? (bUrl || kUrl) : kUrl;
              if (!url && !isDesktopApp()) continue;
              void printRunner([
                `*** ${st === 'bar' ? 'БАР' : 'КУХНЯ'} ***`,
                `Чек №${order.number}${table ? ` · ${tt('table')} ${table.name}` : ''}`,
                new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
                '────────────────',
                ...lines,
              ], url, st);
            }
          }
          const menuUrl = localStorage.getItem(LS.menuUrl) || '';
          const qrFeedback = menuUrl
            ? `${menuUrl}?fb=${order.orderId}`
            : undefined;
          const printedLines = receiptLines({
            lang: (localStorage.getItem('dastarhan.receiptLang') as 'ru' | 'kk') ?? 'ru',
            feedbackQr: Boolean(qrFeedback),
            place: localStorage.getItem(LS.place) ?? 'Dastarhan',
            ...(order.openedBy?.name ? { waiter: order.openedBy.name } : {}),
            openedAt: new Date(order.openedAt),
            number: order.number,
            cashier: user.fullName,
            ...(order.mode === 'DINE_IN' && table ? { table: table.name } : {}),
            at: new Date(),
            items: live,
            ...(allPromos.length > 0 ? { promos: allPromos } : {}),
            ...(discount > 0 ? { discount } : {}),
          ...(t.service > 0 ? { service: t.service, servicePct: t.servicePct } : {}),
            total: t.total,
            ...(deposit && deposit.amount > 0 ? { deposit: Math.min(deposit.amount, t.total) } : {}),
            ...(referralGift ? { referralCode: referralGift } : {}),
            payments,
          }, paperWidth());
          // Строки сохраняем: копия должна быть ТОЙ ЖЕ, а не пересобранной —
          // иначе между оригиналом и копией найдётся различие
          setLastLines(printedLines);
          void printSmart(printedLines, qrFeedback);
          // Сдача считается по наличной части: карта сдачи не даёт
          const cashPart = payments.find((pp) => pp.method === 'CASH');
          setPaid({
            number: order.number,
            total: t.total,
            change: cashPart?.tenderedCash
              ? Math.max(0, cashPart.tenderedCash - cashPart.amount) : 0,
            positions: live.length,
            tableName: table?.name ?? null,
          });
          // Чек закрыт: в зале смотрим оставшиеся столы,
          // за стойкой сразу набираем следующий
          if (afterOrder === 'list') setTimeout(() => setOpenTables(true), 8000);

          // Купон сгорает вместе с чеком; постоянная карта остаётся жить.
          // Решает сервер — он знает вид карты, а касса только сообщает
          if (card && useCard) {
            void fetch(`${BASE}/cards/redeem`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ cardId: card.cardId, orderId: order.orderId }),
            }).catch(() => undefined);
          }
          // Карта относится к этому гостю: следующий чек начинается чистым
          setCard(null);
          // Право на оплату подтверждают на КАЖДЫЙ чек: один раз
          // позвали кассира — один чек закрыли
          setPayApproved(false);

          // Номер зафиксирован оплатой; следующий заказ открыт сразу
          commitNumber(shiftId, order.number);
          setOrder(newOrder(shiftId));
          setTable(null);
          setDiscount(0);
          setDeposit(null);
          setPromoApplied(null);
          setGuest(null);
          setPaying(false);
        }}
      />
      {openTables && (
        <OpenTablesScreen token={token} meId={user.id}
          meIsManager={policyOf(perms, 'table.any') === 'allowed'}
          canDelete={policyOf(perms, 'order.delete') === 'allowed'}
          onBack={() => setOpenTables(false)}
          onOpen={(row, approver) => {
            setOpenTables(false);
            void takeRemote(row);
            if (approver) {
              // Вмешательство в чужой стол — событие для владельца,
              // а не тихая операция: он должен видеть, кто и когда
              setToast(`Открыт стол ${row.tableName} (${row.openedByName})`);
            }
          }} />
      )}
      {paid && (
        <PaidScreen info={paid}
          onClose={() => setPaid(null)}
          onCopy={() => {
            if (lastLines.length > 0) void printSmart(lastLines);
          }} />
      )}
      {weighing && (
        <WeightDialog name={weighing.p.name} unitPrice={weighing.p.price} unit={weighing.p.unit}
          onCancel={() => setWeighing(null)}
          onConfirm={(qty) => {
            setOrder((o) => reduceOrder(o, {
              type: 'item.added', itemId: ulid(), productId: weighing.p.id,
              name: weighing.p.name, qty, unitPrice: weighing.p.price,
              modifiers: weighing.mods,
            }));
            setWeighing(null);
          }}
        />
      )}
      {splitting && (
        <SplitDialog items={order.items.filter((i) => !i.removed)}
          onCancel={() => setSplitting(false)}
          onConfirm={splitOrder}
        />
      )}
      {pickingGuest && (
        <GuestDialog token={token}
          onCancel={() => setPickingGuest(false)}
          onPick={(g) => { setGuest(g); setPickingGuest(false); }}
          onRepeat={(g) => {
            setGuest(g);
            setPickingGuest(false);
            // Повтор — по ТЕКУЩЕМУ каталогу: цены сегодняшние,
            // исчезнувшее и стоп честно пропускаем и говорим об этом
            const products = new Map(
              (catalog.data?.products ?? []).map((p) => [p.id, p] as const),
            );
            let added = 0;
            let skipped = 0;
            let next = order;
            for (const it of g.lastOrderItems) {
              const p = it.productId ? products.get(it.productId) : undefined;
              const inStop = p ? p.id in (catalog.data?.stopList ?? {})
                && (catalog.data?.stopList[p.id] ?? null) === null : false;
              if (!p || inStop) { skipped += 1; continue; }
              next = reduceOrder(next, {
                type: 'item.added', itemId: ulid(), productId: p.id,
                name: p.name, qty: it.qty, unitPrice: p.price, modifiers: [],
              });
              added += 1;
            }
            setOrder(next);
            if (skipped > 0) {
              alert(`Добавлено: ${added}. Недоступно сегодня: ${skipped} — их пропустили.`);
            }
          }}
        />
      )}
      {discountFlow === 'elevate' && (
        <ElevateDialog title="Скидка на чек"
          onCancel={() => setDiscountFlow(null)}
          onConfirm={async (pin) => {
            try {
              const r = await api.elevate(token, { pin, action: 'order.discount' });
              if (pendingDiscount !== null) {
                setDiscount(pendingDiscount);
                setPendingDiscount(null);
                setDiscountFlow(null);
              } else {
                setDiscountFlow('dialog');
              }
              // ваучер сервера прикладывается к событию возврата
              if (r.approverToken) localStorage.setItem('dastarhan.approverToken', r.approverToken);
              return r.approver;
            } catch { return null; }
          }}
        />
      )}
      {discountFlow === 'dialog' && (
        <DiscountDialog subtotal={orderTotals(order).subtotal} current={discount}
          onCancel={() => setDiscountFlow(null)}
          limitPct={discountLimitPct}
          onApply={(v) => {
            const cap = discountLimitPct === null
              ? null
              : Math.round((orderTotals(order).subtotal * discountLimitPct) / 100);
            if (cap !== null && v > cap) {
              // Потолок роли: сверх — только с PIN старшего
              setPendingDiscount(v);
              setDiscountFlow('elevate');
              return;
            }
            setDiscount(v);
            setDiscountFlow(null);
          }}
        />
      )}
      </>
    );
  }

  return (
    <>
    {(calls.data?.rows.length ?? 0) > 0 && (
      <div className="calls-bar">
        {calls.data!.rows.slice(0, 3).map((c) => {
          const waitMin = Math.floor((Date.now() - new Date(c.at).getTime()) / 60_000);
          return (
            <button key={c.id}
              className={`calls-item ${waitMin >= 5 ? 'calls-late' : ''}`}
              title={waitMin >= 5 ? 'Гость ждёт дольше пяти минут' : undefined}
              onClick={() => ackCall(c.id)}>
              🔔 {tt('table')} {c.tableName} — {c.kind === 'BILL' ? tt('callBill') : tt('callWaiter')}
              {waitMin >= 1 ? ` · ждут ${waitMin} мин` : ''} · OK
            </button>
          );
        })}
      </div>
    )}
    {(() => {
      /*
       * Страж забытой смены. Автозакрытия в 06:00 нет и не будет:
       * Z — акт гашения с пересчётом живых денег, автомат ящик не
       * пересчитает. Но если смена открыта до утренней границы —
       * сегодняшние чеки поедут во вчерашнюю, и отчёты сольются.
       * Кричим заметно и ведём к закрытию, не блокируя работу
       */
      const at = localStorage.getItem('dastarhan.shiftOpenedAt');
      if (!at) return null;
      const opened = new Date(at);
      const boundary = new Date();
      boundary.setHours(6, 0, 0, 0);
      if (opened >= boundary || Date.now() < boundary.getTime()) return null;
      return (
        <button className="stale-banner" onClick={() => setShiftSheet(true)}>
          ⚠ Смена открыта {opened.toLocaleDateString('ru-RU',
            { day: '2-digit', month: '2-digit' })} в {opened.toLocaleTimeString('ru-RU',
            { hour: '2-digit', minute: '2-digit' })} — вчерашняя.
          Сегодняшние чеки идут в неё. Закройте смену (Z) и откройте новую →
        </button>
      );
    })()}
    <OrderScreen order={order} catalog={catalog.data}
      shiftHours={(() => {
        const at = localStorage.getItem('dastarhan.shiftOpenedAt');
        return at ? Math.floor((Date.now() - new Date(at).getTime()) / 3600_000) : null;
      })()}
      shiftSum={shiftSum}
      cashier={user.fullName} guestName={guest?.name ?? null}
      depositNote={deposit?.amount ?? null} online={online} queued={queued}
      onAdd={(p, mods) => {
        if (!canOrder) { say('Приём заказов вам не разрешён'); return; }
        pushUndo(order);
        // остаток по стоп-листу: предупредить ДО того, как гость услышит «нет»
        {
          const left = catalog.data?.stopList[p.id];
          if (typeof left === 'number' && left > 0 && left <= 3) {
            say(`«${p.name}» — осталось ${left}`);
          }
        }
        // Весовые позиции спрашивают вес — руками или с весов
        if (p.unit === 'KG' || p.unit === 'L') {
          setWeighing({ p: { id: p.id, name: p.name, price: p.price, unit: p.unit }, mods });
          return;
        }
        setOrder((o) => reduceOrder(o, {
          type: 'item.added', itemId: ulid(), productId: p.id,
          name: p.name, qty: 1, unitPrice: p.price, modifiers: mods,
        }));
      }}
      onRepeat={(itemId) => {
        const cur = order.items.find((x) => x.itemId === itemId && !x.removed);
        if (!cur) return;
        const v = canTouchLine(cur, 'qty.up', senior);
        if (!v.ok) { say(v.reason); return; }
        pushUndo(order);
        /*
         * Отправленное количество неприкосновенно даже для старшего:
         * «ещё одну» после «Заказать» — это ДОЗАКАЗ отдельной свежей
         * строкой, только так кухня узнает про добор. Прежний путь
         * пересобирал строку и тихо снимал то, что уже готовят
         */
        if (cur.fired) {
          setOrder((o) => reduceOrder(o, {
            type: 'item.added', itemId: ulid(), productId: cur.productId,
            name: cur.name, qty: 1, unitPrice: cur.unitPrice,
            modifiers: cur.modifiers,
          }));
          say('Добор — новой строкой: уедет с «Заказать»');
          return;
        }
        // Свежую «ещё одну» пересобираем: снимаем старую и ставим
        // новую с большим количеством
        let next = reduceOrder(order, { type: 'item.removed', itemId });
        next = reduceOrder(next, {
          type: 'item.added', itemId: ulid(), productId: cur.productId,
          name: cur.name, qty: cur.qty + 1, unitPrice: cur.unitPrice,
          modifiers: cur.modifiers,
        });
        setOrder(next);
      }}
      onQueuedTap={() => {
        // Кассир жмёт сюда, когда тревожится за деньги. Ответ должен
        // называть причину: «сервер не отвечает» и «сервер не принял
        // такое событие» лечатся по-разному
        void flushEx(token).then(({ left, reached }) => {
          setQueued(left);
          setOnline(reached);
          if (left === 0) {
            alert('Всё доехало: очередь пуста. ✓');
          } else if (!reached) {
            alert(
              `Не отправлено: ${left}.\n\n`
              + 'Сервер не отвечает — проверьте интернет. События лежат '
              + 'на кассе и уедут сами, как только связь вернётся. '
              + 'Деньги и чеки не теряются.',
            );
          } else {
            alert(
              `Не отправлено: ${left}.\n\n`
              + 'Связь есть, но сервер не принял часть событий. '
              + 'Покажите этот экран владельцу — суммы на месте, '
              + 'их видно в «Чеках».',
            );
          }
        });
      }}
      onNoteAll={() => {
        // Заметка всему заказу задевает и отправленные строки.
        // Официанту показываем причину, старшему разрешаем
        if (!senior && hasFired(order.items)) {
          say('Часть блюд уже на кухне — заметка их не догонит');
          return;
        }
        setNoteFor({ itemId: null, initial: '' });
      }}
      onNote={(itemId: string) => {
        const cur = order.items.find((x) => x.itemId === itemId);
        if (!cur) return;
        // Заметка после отправки бессмысленна: бегунок на кухне,
        // повар его прочитал и уже работает
        const v = canTouchLine(cur, 'note', senior);
        if (!v.ok) { say(v.reason); return; }
        const note = cur.modifiers.find((m) => m.priceDelta === 0)?.name ?? '';
        setNoteFor({ itemId, initial: note });
      }}
      onLineRemove={(itemId: string) => {
        // Причину спрашиваем до снятия: потом человек уже занят
        // следующим гостем и напишет «другое» лишь бы отвязались
        const it = order.items.find((x) => x.itemId === itemId && !x.removed);
        if (!it) return;
        // Отправленное на кухню официант не снимает: продукты потрачены,
        // и это уже вопрос списания, а не набора заказа
        const verdict = canTouchLine(it, 'remove', senior);
        if (!verdict.ok) { say(verdict.reason); return; }
        setVoidFlow({ preselect: itemId });
      }}
      onCourse={(itemId: string) => {
        const cur0 = order.items.find((x) => x.itemId === itemId && !x.removed);
        if (cur0) {
          const v = canTouchLine(cur0, 'course', senior);
          if (!v.ok) { say(v.reason); return; }
        }
        // Курс переключается по кругу: 1 → 2 → 3 → 4 → срочно → 1.
        // Диалог здесь лишний — официант жмёт на бегу
        setOrder((o) => {
          const cur = o?.items.find((x) => x.itemId === itemId)?.course ?? 1;
          const next = cur === 0 ? 1 : cur >= 4 ? 0 : cur + 1;
          return reduceOrder(o, { type: 'item.course', itemId, course: next });
        });
      }}
      onLineDown={(itemId: string) => {
        const cur = order.items.find((x) => x.itemId === itemId && !x.removed);
        if (!cur) return;
        const v = canTouchLine(cur, 'qty.down', senior);
        if (!v.ok) { say(v.reason); return; }
        pushUndo(order);
        /*
         * Крестик тает по одной, а с последней порции снимает строку
         * целиком: официанту не нужно гадать «убавить или удалить» —
         * жмёт крестик, пока блюдо не исчезнет. До отправки это простое
         * снятие без спроса; отправленную строку крестик не трогает
         * (кнопка погашена), там правит только старший через свой путь
         */
        if (cur.qty <= 1) {
          setOrder((oo) => reduceOrder(oo, { type: 'item.removed', itemId }));
          setToast('Убрано из чека');
          return;
        }
        setOrder((oo) => reduceOrder(oo, { type: 'item.qty', itemId, qty: cur.qty - 1 }));
      }}
      onLineBump={(itemId: string) => {
        const cur0 = order.items.find((x) => x.itemId === itemId && !x.removed);
        if (cur0) {
          const v = canTouchLine(cur0, 'qty.up', senior);
          if (!v.ok) { say(v.reason); return; }
        }
        pushUndo(order);
        const cur = order.items.find((x) => x.itemId === itemId && !x.removed);
        if (!cur) return;
        // Двойной тап по отправленной — тот же дозаказ новой строкой
        if (cur.fired) {
          setOrder((oo) => reduceOrder(oo, {
            type: 'item.added', itemId: ulid(), productId: cur.productId,
            name: cur.name, qty: 1, unitPrice: cur.unitPrice,
            modifiers: cur.modifiers,
          }));
          say('Добор — новой строкой: уедет с «Заказать»');
          return;
        }
        setOrder((oo) => reduceOrder(oo, { type: 'item.qty', itemId, qty: cur.qty + 1 }));
      }}
      onLineTap={(itemId) => {
        // Касание строки — самый частый жест, и он снимает позицию.
        // Для отправленной на кухню это тихое списание того, что уже
        // готовят: проверяем ДО всего остального
        const tapped = order.items.find((x) => x.itemId === itemId && !x.removed);
        if (tapped) {
          const v = canTouchLine(tapped, 'remove', senior);
          if (!v.ok) { say(v.reason); return; }
        }

        const policy = policyOf(perms, 'order.item.remove');
        if (policy === 'allowed' || policy === 'self_pin') {
          pushUndo(order);
          setOrder((o) => reduceOrder(o, { type: 'item.removed', itemId }));
          say('Позиция снята', () => {
            const prev = undoStack.current.pop();
            if (prev) setOrder(prev);
          });
        } else if (policy === 'elevated_pin') {
          if (!navigator.onLine) {
            alert('Снятие позиции подтверждает менеджер — для проверки PIN нужна связь.');
            return;
          }
          setElevateFor(itemId);
        }
        // denied: строка просто не снимается — касса не спорит, а не пускает
      }}
      onPay={openPay}
      onMode={(mode: OrderMode) => {
        setOrder((o) => reduceOrder(o, { type: 'mode', mode }));
        say(mode === 'DINE_IN' ? 'Режим: в зале'
          : mode === 'TAKEAWAY' ? 'Режим: с собой' : 'Режим: доставка');
        // «Весь стол уходит»: упаковали остатки — стол пора отдать
        if (mode === 'TAKEAWAY' && table
          && window.confirm(`Заказ теперь «с собой». Освободить стол ${table.name}?`)) {
          setTable(null);
        }
      }}
      table={table?.name ?? null}
      onPickTable={() => setPickingTable(true)}
      onShowMap={() => setShowMap(true)}
      onReassign={() => setReassignOpen(true)}
      canVoid={policyOf(perms, 'order.item.remove') !== 'denied'}
      needsPinToVoid={policyOf(perms, 'order.item.remove') === 'elevated_pin'}
      onVoidPick={() => setVoidFlow({ preselect: null })}
      canReassign={policyOf(perms, 'table.reassign') === 'allowed'}
      onTransfer={() => {
        if (policyOf(perms, 'table.manage') === 'denied') {
          say('Перенос столов доступен старшему');
          return;
        }
        setTransferOpen(true);
      }}
      onFire={() => void fireToKitchen()}
      onOpenTables={() => setOpenTables(true)}
      onCard={() => setCardOpen(true)}
      onExit={() => {
        // Набранный заказ при выходе пропал бы вместе с экраном.
        // Предлагаем отложить: стол останется открытым, сменщик
        // подхватит его через «Открытые столы»
        const live = order.items.filter((i) => !i.removed).length;
        if (live > 0) {
          if (!window.confirm(
            `В заказе ${live} поз. Отложить на стол и выйти?\n\n`
            + 'Отмена — останетесь в кассе.',
          )) return;
          // Отложить можно только заказ на столе: без стола его некуда класть
          if (features.tables && order.mode === 'DINE_IN' && table) parkCurrent();
        }
        /*
         * Выход из кассы стирает черновик.
         *
         * Черновик придуман, чтобы пережить внезапную перезагрузку — но
         * не смену человека. Кассир выходил, входил снова и оказывался
         * на чужом столе с чужим набором; тап по другому столу уводил
         * этот заказ туда (Дым, 17.08). За кассой посменно работают
         * разные люди, и новый должен начинать с чистого экрана.
         */
        try {
          const sid = localStorage.getItem(LS.shiftId) ?? 'draft';
          localStorage.removeItem(draftKey(sid));
        } catch { /* хранилище недоступно — выход всё равно должен случиться */ }
        onLogout();
      }}
      freshCount={freshCount(order.items)}
      totals={t}
      features={features}
      waiter={waiter}
      senior={senior}
      parkedCount={parked.length}
      canPark={features.tables && order.mode === 'DINE_IN' && !!table && liveCount > 0}
      onPark={() => {
        // «Заказать и выйти» — три действия одной кнопкой: на кухню,
        // отложить стол и запереть сессию. Официант уходит в зал,
        // планшет остаётся на стойке — следующий вводит свой PIN,
        // и чужие столы не открываются под чужим именем
        void (async () => {
          if (waiter && features.kitchen && freshCount(order.items) > 0) {
            await fireToKitchen();
          }
          parkCurrent();
          if (waiter) window.dispatchEvent(new Event('dastarhan:lock'));
        })();
      }}
      precheckStale={precheckSnap !== null && precheckSnap !== t.total}
      onPrecheck={() => setBillOpen(true)}
      onCloseShift={() => {
        // В закрытии смены сходится выручка дня и считается ящик.
        // Официант этого не делает: он не отвечает за наличные
        if (policyOf(perms, 'cash.shift.close') === 'denied') {
          say('Смену закрывает старший');
          return;
        }
        // «Смена» = центр отчётности: X, акт, прошлые — и закрытие
        setShiftSheet(true);
      }}
      onChecks={() => setViewChecks(true)}
      onPrinter={() => setPrinterOpen(true)}
      twinTab={twinTab}
      keysHelp={keysHelp}
      onKeysHelpClose={() => setKeysHelp(false)}
      toast={toast}
      onToastUndo={toastUndo ? () => {
        toastUndo();
        setToast(null);
        setToastUndo(null);
      } : null}
      stopCount={Object.keys(catalog.data?.stopList ?? {}).length}
      onStopList={() => {
        // Стоп-лист останавливает продажу для всего зала: официант
        // не должен решать это в одиночку
        if (policyOf(perms, 'stoplist.manage') === 'denied') {
          say('Стоп-лист ведёт старший');
          return;
        }
        setStopOpen(true);
      }}
      extra={<>
        {reassignOpen && table && (
        <ReassignDialog token={token} tableName={table.name}
          onCancel={() => setReassignOpen(false)}
          onDone={async (toUserId, toName) => {
            await fetch(`${BASE}/open-orders/reassign`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ orderId: order.orderId, toUserId }),
            });
            setReassignOpen(false);
            // Стол ушёл — касса освобождается: продолжать вести чужой
            // заказ нельзя, он теперь принадлежит сменщику
            setOrder(newOrder(localStorage.getItem(LS.shiftId) ?? 'draft'));
            setTable(null);
            setDiscount(0);
            say(`Стол передан: ${toName}`);
          }} />
        )}
        {/* Окна рабочего экрана. Раньше они жили ВНУТРИ ветки оплаты:
            до неё их просто не существовало, и нажатие кнопки молча
            ничего не делало — счёт, снятие позиции, заметка, промокод */}
        {billOpen && (
        <BillDialog
          order={order}
          table={table}
          guestName={guest?.name ?? guest?.phone ?? null}
          totals={{
            subtotal: t.subtotal, discount: discount,
            service: t.service, servicePct: t.servicePct, total: t.total,
          }}
          promos={allPromos}
          canPay={payPolicy === 'allowed'}
          canUnbill={policyOf(perms, 'table.unbill') === 'allowed'}
          billed={precheckAt !== null || takenTable}
          onUnbill={() => {
            void (async () => {
              const res = await fetch(`${BASE}/open-orders/${order.orderId}/unbill`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` },
              });
              if (!res.ok) { say('Не получилось вернуть счёт'); return; }
              setPrecheckAt(null);
              setPrecheckSnap(null);
              setBillOpen(false);
              say('Стол вернулся официанту — можно дозаказывать');
            })();
          }}
          placeName={localStorage.getItem(LS.place) ?? 'Дастархан'}
          printedAt={precheckAt !== null
            ? new Date(precheckAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
            : null}
          stale={precheckSnap !== null && precheckSnap !== t.total}
          cashierName={user.fullName}
          freshCount={freshCount(order.items)}
          onSplit={features.tables && !!table && liveCount > 1
            ? () => { setBillOpen(false); setSplitting(true); } : null}
          onComment={(text) => setOrder((o) => reduceOrder(o, { type: 'comment', text }))}
          onDiscount={payPolicy === 'allowed' && policyOf(perms, 'order.discount') !== 'denied'
            ? () => {
                setBillOpen(false);
                // Тот же путь, что и в оплате: у кого право с подтверждением —
                // сперва PIN старшего, у остальных сразу окно скидки
                setDiscountFlow(
                  policyOf(perms, 'order.discount') === 'elevated_pin' ? 'elevate' : 'dialog',
                );
              }
            : null}
          onCard={payPolicy === 'allowed' ? () => { setBillOpen(false); setCardOpen(true); } : null}
          cardLabel={card ? card.label : null}
          ownerName={order.openedBy && order.openedBy.id !== user.id ? order.openedBy.name : null}
          onGiveBack={() => void giveBack()}
          onBack={() => setBillOpen(false)}
          onPrint={() => {
            // Печать закрывает счёт целиком: гость получил бумагу,
            // стол ждёт денег, официант идёт дальше. Отдельная «печать
            // посмотреть» на практике не понадобилась — если гость
            // дозакажет, счёт перепечатают
            setBillOpen(false);
            doPrecheck(true);
            leaveTable();
          }}
          onClose={() => {
            // «Закрыть счёт» без печати: принтера нет или лента кончилась.
            // Стол всё равно помечается — кассир увидит его в списке
            setBillOpen(false);
            doPrecheck(true, false);
            leaveTable();
          }}
          onPay={() => { setBillOpen(false); openPay(); }} />
        )}
        {/* Отказ от блюда: весь путь — одна машина (src/void).
            PIN старшего, офлайн-пропуск, причина, предохранители —
            внутри неё; здесь только применение результата */}
        {voidFlow && (
          <VoidFlow
            token={token}
            deviceKey={localStorage.getItem(LS.deviceKey) ?? ''}
            terminalId={terminalId}
            order={order}
            needsPin={policyOf(perms, 'order.item.remove') === 'elevated_pin'}
            preselect={voidFlow.preselect}
            onCancel={() => setVoidFlow(null)}
            onDone={({ after, event, toast }) => {
              pushUndo(order);
              setOrder(after);
              /*
               * Три правды об одном столе — локальная, отложенная и
               * серверная — обязаны совпасть сразу после отказа.
               *
               * Пустой чек НЕ зеркалим: сервер получал стол с нулём
               * позиций, оставался «занят», и стол горел зелёным
               * навсегда (Дым, 11.08: «остался зелёным, заказ не
               * убрался»). Пусто — значит стол свободен
               */
              if (table) {
                const live = after.items.filter((i) => !i.removed);
                if (live.length === 0) {
                  mirrorUnpark(token, after.orderId);
                  setParked((ps) => ps.filter((x) => x.order.orderId !== after.orderId));
                } else {
                  mirrorPark(token, {
                    table, order: after, discount,
                    ...(guest ? { guest } : {}),
                  } as ParkedOrder);
                  // отложенная копия того же стола не должна отставать
                  setParked((ps) => ps.map((x) => (x.order.orderId === after.orderId
                    ? { ...x, order: after } : x)));
                }
              }
              void push(event as never);
              setVoidFlow(null);
              say(toast, () => {
                const prev = undoStack.current.pop();
                if (prev) setOrder(prev);
              });
            }}
          />
        )}
        {noteFor && (
        <TextPrompt
          title={noteFor.itemId ? 'Заметка кухне' : 'Заметка кухне — всему заказу'}
          hint="«без лука», «остро», «отдельно» — повар прочитает на бегунке"
          initial={noteFor.initial}
          placeholder="без соли"
          maxLength={60}
          allowEmpty
          onCancel={() => setNoteFor(null)}
          onDone={(v) => {
            const target = noteFor.itemId;
            setOrder((o) => {
              if (!o) return o;
              const apply = (it: typeof o.items[number]) => {
                // Заметка живёт модификатором с нулевой ценой: чек,
                // бегунок и кухонный экран видят её бесплатно
                const mods = it.modifiers.filter((m) => m.priceDelta !== 0);
                return {
                  ...it,
                  modifiers: v ? [...mods, { name: v, priceDelta: 0 }] : mods,
                };
              };
              return {
                ...o,
                items: o.items.map((it) =>
                  !it.removed && (target === null || it.itemId === target) ? apply(it) : it),
              };
            });
            setNoteFor(null);
          }} />
        )}
        {promoPrompt && (
        <TextPrompt
          title="Промокод друга"
          hint="Код из приглашения — скидка встанет в чек"
          placeholder="DRUG-1234"
          maxLength={40}
          onCancel={() => setPromoPrompt(false)}
          onDone={(raw) => {
            setPromoPrompt(false);
            if (!navigator.onLine) { alert(tt('needNet')); return; }
            const code = raw.toUpperCase();
            void api.redeemCode(token, code)
              .then((r) => {
                const amount = Math.round((orderTotals(order).subtotal * r.percent) / 100);
                setDiscount(amount);
                setPromoApplied({ code, amount });
                say(`${tt('codeOk')} ${r.percent}%`);
              })
              .catch(() => alert(tt('codeBad')));
          }} />
        )}
        {payElevate && (
        <ElevateDialog title="Оплату подтверждает кассир"
          onCancel={() => setPayElevate(false)}
          onConfirm={async (pin) => {
            try {
              const r = await api.elevate(token, { pin, action: 'order.pay' });
              setPayElevate(false);
              setPayApproved(true);
              setPaying(true);
              return r.approver;
            } catch { return null; }
          }} />
        )}

        {/* Карта гостя открывается И с рабочего экрана: раньше кнопка
            ставила флаг, а диалог жил только на экране оплаты —
            нажатие молча не делало ничего */}
        {cardOpen && (
          <CardDialog token={token}
            onCancel={() => setCardOpen(false)}
            onApply={(v) => { setCard(v); setCardOpen(false); say(`${v.label} · −${v.percent}%`); }} />
        )}
        {stopOpen && catalog.data && (
          <StopDialog token={token} catalog={catalog.data}
            onChanged={() => void catalog.refetch()}
            onClose={() => setStopOpen(false)} />
        )}
        {printerOpen && (
          <PrinterDialog waiter={waiterMode}
            canDrawer={policyOf(perms, 'cash.drawer') === 'allowed'}
            afterOrder={afterOrder}
            onAfterOrder={(v) => {
              setAfterOrder(v);
              localStorage.setItem('dastarhan.afterOrder', v);
            }}
            onStopList={() => setStopOpen(true)}
            onLock={online ? () => {
              // Смена и заказ живы — заперта только сессия человека
              setPrinterOpen(false);
              window.dispatchEvent(new Event('dastarhan:lock'));
            } : null}
            onXReport={() => void (async () => {
              try {
                const sum = await api.shiftSummary(token, shiftId!);
                const L = [
                  '*** X-ОТЧЁТ (смена открыта) ***',
                  new Date().toLocaleString('ru-RU'),
                  ...(() => {
                    const at = localStorage.getItem('dastarhan.shiftOpenedAt');
                    if (!at) return [];
                    const h = Math.floor((Date.now() - new Date(at).getTime()) / 3600_000);
                    return h >= 1 ? [`Смена идёт: ${h} ч`] : [];
                  })(),
                  '────────────────',
                  sum.checks === 0
                    ? 'Продаж пока не было — смена только началась'
                    : `Чеков: ${sum.checks}`,
                  `Наличные: ${formatMoney(sum.cashSales)}`,
                  `Карта: ${formatMoney(sum.cardSales)}`,
                  `Kaspi QR: ${formatMoney(sum.qrSales)}`,
                  `Ожидается в ящике: ${formatMoney(sum.expectedCash)}`,
                  // на ленте 58мм пропись не влезает в строку — переносим по словам
                  ...wrap(`(${moneyInWords(sum.expectedCash)})`, paperWidth()),
                ];
                if ((sum.tipsCard ?? 0) > 0) L.push(`На чай картой: ${formatMoney(sum.tipsCard!)}`);
                {
                  // стопы на бумаге: зал и кухня читают один лист
                  const stops = Object.keys(catalog.data?.stopList ?? {}).length;
                  if (stops > 0) L.push(`Позиций на стопе: ${stops}`);
                }
                // бумага уходит в зал — пусть несёт и завтрашнюю нагрузку
                try {
                  const d = new Date(Date.now() + 86400_000).toISOString().slice(0, 10);
                  const rows = await api.reservationsOn(token, d);
                  const n = rows.rows.filter((x) => x.status !== 'CANCELLED').length;
                  if (n > 0) L.push(`Броней на завтра: ${n}`);
                } catch { /* нет связи — X-отчёт всё равно печатается */ }
                void printSmart(L);
              } catch { alert('Нет связи: X-отчёт требует сервера.'); }
            })()}
            onCashOp={(kind, amount, comment) => {
              if (policyOf(perms, 'cash.drawer') === 'denied') {
                alert('Денежный ящик открывает старший');
                return;
              }
              void push({
                eventId: ulid(), terminalId, at: new Date().toISOString(),
                type: 'cash.op', shiftId, kind, amount,
                ...(comment ? { comment } : {}),
                byName: user.fullName,
              });
              alert(kind === 'PAY_IN' ? tt('opIn') : tt('opOut'));
            }}
            onWaiter={(v) => {
              // Выйти из режима официанта — значит увидеть деньги.
              // Войти в него можно всегда: это ограничение, а не власть
              if (!v && policyOf(perms, 'pos.mode.switch') === 'denied') {
                alert('Кассовый режим включает старший');
                return;
              }
              setWaiter(v);
              if (v) localStorage.setItem(LS.waiter, '1');
              else localStorage.removeItem(LS.waiter);
            }}
            onClose={() => setPrinterOpen(false)} />
        )}
        {pickingTable && (
          <TableDialog token={token} current={table?.id ?? null} meId={user.id}
            parked={Object.fromEntries(parked.map((x) => [
              x.table.id,
              {
                items: x.order.items.filter((i) => !i.removed).length,
                ageMin: Math.floor((Date.now() - new Date(x.order.openedAt).getTime()) / 60_000),
              },
            ]))}
            onPick={pickTable}
            onPickRemote={(row) => void takeRemote(row)}
            onCancel={() => setPickingTable(false)}
          />
        )}
        {/*
          * Замок подписки.
          *
          * Заведение не заплатило — продажи закрыты. Но смену дать
          * закрыть обязаны: в ящике лежат чужие деньги, их надо
          * пересчитать и снять Z. И баннер не появляется посреди
          * набранного чека — сначала дай дописать и рассчитать гостя
          */}
        {catalog.data?.lock?.kind === 'WARN' && (
          <div className="sub-warn" role="status">
            ⚠ {catalog.data.lock.message}
          </div>
        )}
        {catalog.data?.lock?.blocked && liveCount === 0 && (
          <div className="lock-screen" role="dialog" aria-modal="true">
            <div className="lock-card">
              <h1>Продажи закрыты</h1>
              <p>{catalog.data.lock.message}</p>
              <p className="hint">
                Просрочка {catalog.data.lock.days} дн. Смену можно
                закрыть и снять Z-отчёт — деньги в ящике должны сойтись.
              </p>
              <div className="lock-actions">
                <button className="btn accent" onClick={() => setClosingShift(true)}>
                  Закрыть смену (Z)
                </button>
                <button className="btn" onClick={() => void catalog.refetch()}>
                  Я оплатил — проверить
                </button>
              </div>
            </div>
          </div>
        )}
        {showMap && (
          <HallMap token={token}
            senior={policyOf(perms, 'table.any') === 'allowed'}
            canPay={policyOf(perms, 'order.pay') === 'allowed'}
            onPick={(id, name) => {
              setShowMap(false);
              /*
               * Со схемы красный стол ведёт туда же, куда из списка —
               * в оплату. Иначе кассир нажимает стол, ждущий денег,
               * и попадает в набор блюд: путь зависел от того, откуда
               * он пришёл, хотя стол и намерение одни и те же.
               */
              void (async () => {
                try {
                  const list = await api.openOrders(token);
                  const row = list.rows.find((r) => r.tableId === id);
                  if (row?.awaitingPay) { await takeRemote(row); return; }
                } catch {
                  // Без связи спрашивать некого — открываем как обычно
                }
                pickTable({ id, name });
              })();
            }}
            onClose={() => setShowMap(false)} />
        )}
        {transferOpen && table && (
          <TransferDialog
            order={order}
            token={token}
            currentTableId={table.id}
            onCancel={() => setTransferOpen(false)}
            onTransfer={async (toTableId, itemIds) => {
              /*
               * Сервер переносит только те заказы, о которых знает —
               * то есть отложенные на стол. Кассир же обычно жмёт
               * «Перенос» прямо из набранного чека, и раньше получал
               * глухое «проверьте связь». Теперь заказ сначала
               * зеркалим на сервер (это и есть «отложить на стол»),
               * и только потом переносим
               */
              if (table) {
                await api.openOrderPut(token, {
                  orderId: order.orderId,
                  tableId: table.id,
                  tableName: table.name,
                  number: order.number,
                  openedAt: order.openedAt,
                  items: order.items.filter((i) => !i.removed).map((i) => ({
                    itemId: i.itemId, productId: i.productId, name: i.name,
                    qty: i.qty, unitPrice: i.unitPrice, modifiers: i.modifiers,
                    ...(i.fired ? { fired: true } : {}),
                  })),
                  discount,
                  ...(guest ? { guestPhone: guest.phone } : {}),
                });
              }
              if (itemIds === null) {
                await api.transferOrder(token, { orderId: order.orderId, toTableId });
              } else {
                await api.transferItems(token, { orderId: order.orderId, toTableId, itemIds });
              }
              // Перенесённый стол больше не наш: убираем из отложенных
              setParked((p) => p.filter((x) => x.order.orderId !== order.orderId));
              setTransferOpen(false);
              setOrder(newOrder(localStorage.getItem(LS.shiftId) ?? 'draft'));
              setTable(null);
            }} />
        )}
        {elevateFor && (
        <ElevateDialog title="Снять позицию из чека"
          onCancel={() => setElevateFor(null)}
          onConfirm={async (pin) => {
            try {
              const r = await api.elevate(token, { pin, action: 'order.item.remove' });
              setOrder((o) => reduceOrder(o, { type: 'item.removed', itemId: elevateFor }));
              setElevateFor(null);
              return r.approver;
            } catch { return null; }
          }}
        />
        )}
      </>}
    />
    </>
  );
}

function App() {
  /*
   * Программа на диске не знает, где сервер: страница лежит локально,
   * относительный адрес вёл бы в саму папку. Спрашиваем один раз.
   *
   * В браузере этот экран не появляется никогда: там касса открыта
   * с того же адреса, что и сервер, и спрашивать нечего.
   */
  const [needServer, setNeedServer] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.location.protocol !== 'file:') return false;
    try { return !localStorage.getItem('dastarhan.api'); } catch { return true; }
  });

  const [deviceKey, setDeviceKey] = useState(() => localStorage.getItem(LS.deviceKey));
  const [token, setToken] = useState(() => localStorage.getItem(LS.token));
  useEffect(() => {
    const onLock = () => setUser(null);
    window.addEventListener('dastarhan:lock', onLock);
    return () => window.removeEventListener('dastarhan:lock', onLock);
  }, []);
  const [user, setUser] = useState<{ id: string; fullName: string } | null>(() => {
    const raw = localStorage.getItem(LS.user);
    return raw ? JSON.parse(raw) as { id: string; fullName: string } : null;
  });
  const [perms, setPerms] = useState<PermissionMap>(() => {
    const raw = localStorage.getItem(LS.perms);
    return raw ? JSON.parse(raw) as PermissionMap : {};
  });


  if (needServer) {
    return (
      <ServerSetup onSaved={() => {
        /* Адрес читается один раз при загрузке модуля api.ts —
           перезапускаем страницу, чтобы он вступил в силу */
        window.location.reload();
        setNeedServer(false);
      }} />
    );
  }

  if (!deviceKey) {
    return (
      <Activate onDone={(r) => {
        // Новая точка на том же планшете: чужая смена, чужие принтеры
        // и счётчики — чужие деньги; чистим всё, что привязано к точке
        if (localStorage.getItem(LS.location) && localStorage.getItem(LS.location) !== r.locationId) {
          const oldShift = localStorage.getItem(LS.shiftId);
          if (oldShift) {
            localStorage.removeItem(`dastarhan.seq.${oldShift}`);
            localStorage.removeItem(`dastarhan.shiftSum.${oldShift}`);
          }
          for (const k of [
            LS.shiftId, 'dastarhan.shiftOpenedAt', 'dastarhan.lastSync',
            'dastarhan.printer.kitchen', 'dastarhan.printer.bar', 'dastarhan.printer.agent',
            LS.waiter, LS.menuUrl, 'dastarhan.myTable',
          ]) localStorage.removeItem(k);
        }
        localStorage.setItem(LS.deviceKey, r.deviceKey);
        localStorage.setItem(LS.place, r.locationName);
        localStorage.setItem(LS.terminalId, r.terminalId);
          localStorage.setItem(LS.location, r.locationId);
        setDeviceKey(r.deviceKey);
      }} />
    );
  }

  if (!token || !user) {
    return (
      <Pin place={localStorage.getItem(LS.place) ?? 'Касса'}
        onClockOut={async (pin): Promise<ClockOutResult> => {
          try {
            const r = await api.posClockOut({ deviceKey, pin });
            return { ok: true, ...r };
          } catch (e) {
            const noopen = e instanceof ApiError && e.code === 'NO_OPEN_ATTENDANCE';
            return { ok: false, reason: noopen ? 'noopen' : 'pin' };
          }
        }}
        onSubmit={async (pin) => {
          const print = await pinPrint(pin, deviceKey);
          try {
            const s = await api.posLogin({ deviceKey, pin });
            localStorage.setItem(LS.token, s.token);
            localStorage.setItem(LS.user, JSON.stringify(s.user));
            localStorage.setItem(LS.perms, JSON.stringify(s.permissions));
            localStorage.setItem(LS.discountLimit,
              s.discountLimitPct === null ? '' : String(s.discountLimitPct));
            localStorage.setItem(LS.referral,
              s.referral ? JSON.stringify(s.referral) : '');
            localStorage.setItem(LS.menuUrl, s.menuUrl ?? '');
            // Пропуск в офлайн: интернет в зале пропадает, смена — нет
            savePass(print, {
              token: s.token, user: s.user, permissions: s.permissions,
              discountLimitPct: s.discountLimitPct,
            });
            setToken(s.token);
            setUser(s.user);
            setPerms(s.permissions);
            return true;
          } catch (e) {
            if (e instanceof ApiError) {
              // Пауза после перебора — не ошибка кассира, а защита:
              // говорим сколько ждать, чтобы не долбили кнопки вслепую
              const sec = (e as { body?: { seconds?: number } })?.body?.seconds;
              if (sec) return `Слишком много попыток — подождите ${sec} сек`;
              return false;
            }
            /*
             * Связи нет. Касса обязана открываться и в пещере — иначе
             * «интернет упал» превращается в «зал встал». Пускаем по
             * локальному пропуску того, кто уже входил на этой кассе
             */
            const hit = readPass(print);
            if (hit) {
              localStorage.setItem(LS.token, hit.token);
              localStorage.setItem(LS.user, JSON.stringify(hit.user));
              localStorage.setItem(LS.perms, JSON.stringify(hit.permissions));
              localStorage.setItem(LS.discountLimit,
                hit.discountLimitPct === null ? '' : String(hit.discountLimitPct));
              setToken(hit.token);
              setUser(hit.user);
              setPerms(hit.permissions);
              return true;
            }
            return 'Нет связи. Офлайн-вход доступен тем, кто уже входил на этой кассе';
          }
        }} />
    );
  }

  return (
    <>
      <Pos token={token} user={user} perms={perms}
        onLogout={() => {
          localStorage.removeItem(LS.token);
          localStorage.removeItem(LS.user);
          setToken(null); setUser(null);
        }} />
      <IdleLock minutes={3} userName={user.fullName} />
    </>
  );
}

/**
 * Замок экрана при простое.
 *
 * Официант отошёл — и любой может пробить чек от его имени. Но выходить
 * насовсем нельзя: незаконченный заказ пропадёт, а гость сидит за столом.
 *
 * Поэтому касса не выходит, а ЗАПИРАЕТСЯ: поверх экрана ложится замок,
 * сама касса со всем набранным остаётся живой под ним. Ввёл PIN —
 * продолжил с той же строки.
 *
 * Три минуты: меньше — мешает разговаривать с гостем, больше —
 * уже небезопасно в зале.
 */
function IdleLock({ minutes, userName }: { minutes: number; userName: string }) {
  const [locked, setLocked] = useState(false);
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (locked) return;
    let timer: number;
    const reset = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setLocked(true), minutes * 60_000);
    };
    const events = ['pointerdown', 'keydown', 'touchstart'] as const;
    for (const e of events) window.addEventListener(e, reset, { passive: true });
    reset();
    return () => {
      window.clearTimeout(timer);
      for (const e of events) window.removeEventListener(e, reset);
    };
  }, [minutes, locked]);

  const unlock = async (code: string) => {
    setErr('');
    const deviceKey = localStorage.getItem(LS.deviceKey);
    if (!deviceKey) { setLocked(false); return; }
    try {
      const res = await fetch(`${BASE}/auth/pos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceKey, pin: code }),
      });
      if (!res.ok) { setErr('PIN не подошёл'); setPin(''); return; }

      // Ответ несёт свежие права — сохраняем. Владелец мог сменить роль,
      // пока касса стояла запертой, и работать по старым правам после
      // этого нельзя: смысл смены роли как раз в том, чтобы она подействовала
      const s2 = await res.json() as {
        token: string; permissions: Record<string, string>;
        discountLimitPct: number | null;
      };
      localStorage.setItem(LS.token, s2.token);
      localStorage.setItem(LS.perms, JSON.stringify(s2.permissions));
      localStorage.setItem(LS.discountLimit,
        s2.discountLimitPct === null ? '' : String(s2.discountLimitPct));

      setLocked(false); setPin('');
      // Перечитываем состояние: права уже в хранилище, но экран
      // держит прежние в памяти
      window.location.reload();
    } catch {
      // Связи нет — не запираем человека посреди смены:
      // заказ важнее строгости, а устройство уже привязано к заведению
      setLocked(false); setPin('');
    }
  };

  if (!locked) return null;

  const tap = (n: string) => {
    const next = (pin + n).slice(0, 6);
    setPin(next);
    if (next.length >= 4) void unlock(next);
  };

  return (
    <div className="idle-lock">
      <div className="idle-card">
        <span className="eyebrow">{localStorage.getItem(LS.place) ?? 'Касса'}</span>
        <h2>Касса заперта</h2>
        {/* Главный ответ замка — «заказ цел»: человек вернулся и должен
            понять это за секунду, не вчитываясь в мелкий текст */}
        <p className="idle-safe">✓ Заказ сохранён — всё на месте</p>
        <p className="hint">Введите PIN — продолжите с той же строки.</p>
        <p className="idle-who">{userName}</p>
        <p className="dots">{'•'.repeat(pin.length).padEnd(4, '·')}</p>
        {err && <p className="error">{err}</p>}
        <div className="idle-pad">
          {['1','2','3','4','5','6','7','8','9'].map((n) => (
            <button key={n} onClick={() => tap(n)}>{n}</button>
          ))}
          <button className="ghost" onClick={() => setPin('')}>C</button>
          <button onClick={() => tap('0')}>0</button>
          <button className="ghost" onClick={() => setPin((x) => x.slice(0, -1))}>←</button>
        </div>
      </div>
    </div>
  );
}

installCrashReporter('pos');

// Офлайн-оболочка: без неё приложение не откроется без сети
// Область действия берём из адреса: /pos/, /kds/, /menu/ — или корень
registerServiceWorker();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <UpdateBanner />
    <QueryClientProvider client={qc}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
